import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ComponentRef, EventEmitter, HostListener, Input, Output, OnChanges, SimpleChanges, OnDestroy, ViewChild, ViewContainerRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { Observable, Subject, BehaviorSubject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzResultModule } from 'ng-zorro-antd/result';
import { FikoYTId } from '../../../helpers/FikoYTId';
import { NzInputModule, NzInputPrefixDirective } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTypographyModule } from 'ng-zorro-antd/typography';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzDropdownModule } from 'ng-zorro-antd/dropdown';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzListModule } from 'ng-zorro-antd/list';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { DatePipe } from '@angular/common';
import { FikoYTKokBilesenComponent } from '../../../../kok-bilesen/kok-bilesen.component';
import { IAcılırKutuParametre, IKokParametre, IParametre, IParametreTumu, ISekmeParametre, ITurParametre, kontrolDeger, parametreAyikla } from '../../../interfaces/IParametre/IParametre';
import { OrnekParametreler } from './IOrnekParametreler';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzModalModule } from 'ng-zorro-antd/modal';
/** TS tip anotasyonlarını kaldırarak saf JS'e çevirir (ts.transpile yerine hafif çözüm) */
function tipAnotasyonlarinSoy(tsCode: string): string {
  return tsCode.replace(/:\s*(?:any|string|number|boolean|void|never|object|unknown)(?:\[\])?\s*/g, ' ');
}
import { FikoYTIstekKok } from '@/src/service/modules/fikoyt-istek-kok/fikoyt.istek.makinesi.service';
import { escapeRegex } from '@/src/app/shared/utils/regex.utils';
import * as MailUI from '@/src/app/shared/utils/mail-ui.utils';
import { debounce } from '../../../decorators/debounce';
import { CdkDragDrop, CdkDropList, CdkDrag, CdkDragHandle, CdkDragPlaceholder, moveItemInArray } from '@angular/cdk/drag-drop';
// Modal editör kaldırıldı - tüm düzenleme inline gelişmiş ayarlar panelinden yapılıyor
import { ParametreValidationService } from './components/parametre-validation.service';
import { ParametreHizliEkleComponent } from './parametre-hizli-ekle/parametre-hizli-ekle.component';
import { DinamikBilesenRendererComponent } from '../dinamik-bilesen/dinamik-bilesen-renderer.component';
import { DosyaKutusuComponent } from './dosya-kutusu/dosya-kutusu.component';
// NOT: Aşağıdaki component'ler gelecekte kullanılmak üzere hazır:
// import { ParametreToolbarComponent } from './components/parametre-toolbar.component';
// import { ParametreItemComponent } from './components/parametre-item.component';
// import { ParametreFieldWrapperComponent } from './components/parametre-field-wrapper.component';
// import { ParametreInputWrapperComponent } from './components/parametre-input-wrapper.component';

declare var $: any;

function runDynamicCode(jsCode: string, args: any[]): any {
  if (!jsCode || typeof jsCode !== 'string') return undefined;
  const yasakli = /\beval\s*\(|new\s+Function|(?:^|[^.])import\s*\(|\brequire\s*\(|\bfetch\s*\(|XMLHttp|document\.|window\./;
  if (yasakli.test(jsCode)) { console.warn('[Parametre] Güvensiz kod engellendi'); return undefined; }
  try {
    const executor = new Function('args', `"use strict"; return (${jsCode})(...args);`);
    return executor(args);
  } catch (e) { console.warn('[Parametre] Kod çalıştırma hatası:', e); return undefined; }
}

@Component({
  selector: 'fikoyt-parametreler',
  imports: [
    CommonModule,
    FormsModule,
    NzInputModule,
    NzSelectModule,
    NzButtonModule,
    NzTabsModule,
    NzTypographyModule,
    NzIconModule,
    NzTagModule,
    NzProgressModule,
    NzRadioModule,
    NzDatePickerModule,
    NzCheckboxModule,
    NzResultModule,
    NzModalModule,
    NzDropdownModule,
    NzMenuModule,
    NzTooltipModule,
    NzTableModule,
    NzDividerModule,
    NzPopoverModule,
    NzListModule,
    NzEmptyModule,
    NzPopconfirmModule,
    DatePipe,
    ParametreHizliEkleComponent,
    DinamikBilesenRendererComponent,
    DosyaKutusuComponent,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    CdkDragPlaceholder
  ],
  templateUrl: './parametreler.component.html',
  styleUrls: ['./parametreler.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  providers: [ParametreValidationService]
})
export class ParametrelerComponent extends FikoYTKokBilesenComponent implements OnChanges, OnDestroy {
  @Input() override parametreler: IParametreTumu[] = null;
  @Input() sadceGoruntuleme: boolean = false; // Sadece görüntüleme modu
  @Input() kullaniciRolleri: string[] = []; // Kullanıcının rolleri
  @Input() kullaniciId: string = ''; // Kullanıcı ID'si (yetki kontrolü için)
  @Input() yetkiModu: 'tum' | 'rollerineGore' | 'kisiyeOzel' = 'tum'; // Yetki modu
  @Input() performansModu: boolean = true; // Performans optimizasyonu aktif mi
  @Input() formModu: boolean = false; // Sade form görünümü: toolbar, drag, ayar butonları gizli
  @Input() gosterimModu: boolean = false; // Gösterim modu: değerler text olarak gösterilir, tıklayınca inline edit
  @Input() kart: any = null;
  @Input() hizliEkleKaynakSetleri: any[] = [];

  @Output() degisti: EventEmitter<any[]> = new EventEmitter<any[]>();
  @Output() parametrelerChange: EventEmitter<IParametreTumu[]> = new EventEmitter<IParametreTumu[]>();
  @Output() parametreDegisti: EventEmitter<{ parametre: IParametreTumu, deger: any }> = new EventEmitter();
  @Output() hataOlustu: EventEmitter<{ parametre: IParametreTumu, hata: string }> = new EventEmitter();

  // 🚀 Performans optimizasyonu için yeni özellikler
  private destroy$ = new Subject<void>();
  private parametreler$ = new BehaviorSubject<IParametreTumu[]>([]);
  filtrelenmisParametreler$ = new BehaviorSubject<IParametreTumu[]>([]);
  private aramaTerimi$ = new BehaviorSubject<string>('');
  private cache = new Map<string, any>();

  // Virtual scrolling için
  private gosterilenParametreSayisi: number = 50;
  private yuklenenParametreSayisi: number = 0;

  // debounce için
  private degisiklikler$ = new Subject<{ parametre: IParametreTumu, deger: any }>();

  isLoading: boolean = false;
  aramaSonuclari: IParametreTumu[] = [];
  secilenKategori: string = 'tum';
  kategoriler: string[] = ['tum'];
  aramaTerimi: string = '';

  /** Inline ayarları açık olan parametrelerin indeksleri */
  ayarlarAcikSet = new Set<number>();

  /** Gösterim modunda inline düzenleme açık olan parametrelerin indeksleri */
  gosterimDuzenleSet = new Set<number>();

  /** Toolbar görünürlüğü - varsayılan gizli */
  toolbarAcik = false;

  gorunumModu: 'liste' | 'kart' = 'liste';

  private undoStack: string[] = [];
  private redoStack: string[] = [];
  private undoAktif = false;

  /** Hızlı ekle paneli görünürlüğü */
  hizliEkleAcik = false;

  sablonListesi: { isim: string; parametreler: IParametreTumu[]; tarih: string }[] = [];
  sablonModalAcik = false;
  yeniSablonIsim = '';

  kontrolDeger = kontrolDeger;

  hataAnimSatirlar = new Set<number>();

  get zorunluIlerleme(): { doldurulan: number; toplam: number; yuzde: number } {
    if (!this.parametreler?.length) return { doldurulan: 0, toplam: 0, yuzde: 100 };
    const zorunlular = this.parametreler.filter(p => p.ayarlar?.zorunlu);
    if (!zorunlular.length) return { doldurulan: 0, toplam: 0, yuzde: 100 };
    const doldurulan = zorunlular.filter(p => {
      const d = p.degeri;
      if (d == null || d === '' || d === undefined) return false;
      if (Array.isArray(d) && d.length === 0) return false;
      return true;
    }).length;
    return { doldurulan, toplam: zorunlular.length, yuzde: Math.round(doldurulan / zorunlular.length * 100) };
  }

  hataAnimasyonuBaslat(index: number) {
    this.hataAnimSatirlar.add(index);
    this.CD.detectChanges();
    setTimeout(() => { this.hataAnimSatirlar.delete(index); this.CD.detectChanges(); }, 600);
  }

  /** Parametreyi düzenleyebilir mi kontrol et */
  duzenleyebilirMi(parametre: IParametreTumu): boolean {
    // Sadece görüntüleme modunda ise hiçbir değişiklik yapamaz
    if (this.sadceGoruntuleme) return false;

    // Parametre kendisi sadece okunur ise
    if (parametre?.ayarlar?.sadeceOkunur) return false;

    // Yetki moduna göre kontrol et
    switch (this.yetkiModu) {
      case 'tum':
        return true;

      case 'rollerineGore':
        return this.rolTabanliYetkiKontrolu(parametre, 'degistirebilir');

      case 'kisiyeOzel':
        return this.kisiyeOzelYetkiKontrolu(parametre, 'degistirebilir');

      default:
        return false;
    }
  }

  /** Parametreyi silebilir mi kontrol et */
  silebilirMi(parametre: IParametreTumu): boolean {
    // Sadece görüntüleme modunda ise silemez
    if (this.sadceGoruntuleme) return false;

    // Yetki moduna göre kontrol et
    switch (this.yetkiModu) {
      case 'tum':
        return true;

      case 'rollerineGore':
        return this.rolTabanliYetkiKontrolu(parametre, 'silebilir');

      case 'kisiyeOzel':
        return this.kisiyeOzelYetkiKontrolu(parametre, 'silebilir');

      default:
        return false;
    }
  }

  // 🎯 Rol tabanlı yetki kontrolü
  private rolTabanliYetkiKontrolu(parametre: IParametreTumu, yetkiTipi: 'gorebilir' | 'degistirebilir' | 'silebilir'): boolean {
    if (!parametre?.yetkiler) return true; // Yetki belirtilmemişse herkes erişebilir

    const yetkiliRoller = parametre.yetkiler[yetkiTipi] || [];

    // Hiç yetki belirtilmemişse herkes erişebilir
    if (yetkiliRoller.length === 0) return true;

    // Kullanıcının yetkili rollerini kontrol et
    return this.kullaniciRolleri.some(rol => yetkiliRoller.includes(rol));
  }

  // 🎯 Kişiye özel yetki kontrolü
  private kisiyeOzelYetkiKontrolu(parametre: IParametreTumu, yetkiTipi: 'gorebilir' | 'degistirebilir' | 'silebilir'): boolean {
    if (!parametre?.yetkiler) return true;

    const yetkiliKisiler = parametre.yetkiler[yetkiTipi] || [];

    // Kullanıcı ID'si varsa kontrol et
    if (this.kullaniciId && yetkiliKisiler.includes(this.kullaniciId)) {
      return true;
    }

    // Roller de varsa rol tabanlı kontrol yap
    return this.rolTabanliYetkiKontrolu(parametre, yetkiTipi);
  }

  // 🎯 Parametre ekleme yetkisi var mı?
  parametreEkleyebilirMi(): boolean {
    // Sadece görüntüleme modunda değilse
    if (this.sadceGoruntuleme) return false;

    // Yetki moduna göre
    switch (this.yetkiModu) {
      case 'tum':
        return true;
      case 'rollerineGore':
        return this.kullaniciRolleri.includes('admin') ||
          this.kullaniciRolleri.includes('editor') ||
          this.kullaniciRolleri.includes('parametre_yoneticisi');
      case 'kisiyeOzel':
        return this.kullaniciRolleri.includes('parametre_ekleyebilir');
      default:
        return false;
    }
  }

  // 🎯 Yetki mesajı al
  getYetkiMesaji(parametre: IParametreTumu, islem: 'duzenleme' | 'silme' | 'goruntuleme'): string {
    if (!parametre?.yetkiler) return '';

    const yetkiTipi = islem === 'duzenleme' ? 'degistirebilir' :
      islem === 'silme' ? 'silebilir' : 'gorebilir';

    const yetkiliRoller = parametre.yetkiler[yetkiTipi] || [];

    if (yetkiliRoller.length === 0) return '';

    return `Bu işlem için gerekli yetkiler: ${yetkiliRoller.join(', ')}`;
  }

  /** Parametreyi görebilir mi kontrol et */
  gorebilirMi(parametre: IParametreTumu): boolean {
    if (!parametre?.yetkiler?.gorebilir) return true; // Yetki tanımlanmamışsa herkes görebilir
    return parametre.yetkiler.gorebilir.some(rol => this.kullaniciRolleri.includes(rol));
  }

  constructor() {
    super();
    this.initializePerformanceOptimizations();
    this.sablonlariYukle();

    this.degisti.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.parametrelerChange.emit(params);
    });
  }

  // 🚀 Performans optimizasyonlarını başlat
  private initializePerformanceOptimizations(): void {
    if (!this.performansModu) return;

    // Debounce ile değişiklikleri yönet
    this.degisiklikler$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(({ parametre, deger }) => {
      this.parametreDegisti.emit({ parametre, deger });
      this.kontrolDeger(parametre, this.parametreler, this.kart);
      this.CD.detectChanges();
    });

    // Arama fonksiyonunu optimize et
    this.aramaTerimi$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(term => {
      this.aramaYap(term);
    });

    // Parametre değişikliklerini takip et
    this.parametreler$.pipe(
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(params => {
      this.kategorileriOlustur(params);
      this.filtrelenmisParametreler$.next(params);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['parametreler']) {
      this.parametreler$.next(this.parametreler || []);
    }
  }

  /** Gösterim modunda parametreye tıklayınca inline edit aç/kapat */
  gosterimDuzenleToggle(index: number) {
    if (this.gosterimDuzenleSet.has(index)) {
      this.gosterimDuzenleSet.delete(index);
    } else {
      this.gosterimDuzenleSet.add(index);
    }
    this.CD.detectChanges();
  }

  gosterimDuzenleMi(index: number): boolean {
    return this.gosterimDuzenleSet.has(index);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cache.clear();
  }

  // 🚀 Görüntülenecek parametreler (arama durumuna göre)
  get gosterilecekParametreler(): IParametreTumu[] {
    return this.aramaTerimi ? this.aramaSonuclari : (this.filtrelenmisParametreler$.value ?? []);
  }

  // 🚀 Hatalı parametre sayısı
  get hataliParametreSayisi(): number {
    return (this.parametreler || []).filter(p => p.hatalar?.length > 0).length;
  }

  // 🚀 Dosya seçildiğinde
  dosyaSecildi(event: Event, parametre: IParametreTumu): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const dosya = input.files[0];

      // Dosya boyutu kontrolü
      const maksBoyut = (parametre.ayarlar?.maksBoyutMb || 10) * 1024 * 1024;
      if (dosya.size > maksBoyut) {
        this.nzNotification.error('Dosya Boyutu', `Dosya boyutu çok büyük. Maksimum ${parametre.ayarlar?.maksBoyutMb || 10}MB olmalı.`);
        return;
      }

      // Object URL oluştur
      if (parametre.turu === 'Resim' || parametre.turu === 'Video' || parametre.turu === 'Ses') {
        const url = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(dosya));
        parametre.degeri = {
          name: dosya.name,
          size: dosya.size,
          type: dosya.type,
          objectUrl: url
        } as any;
      } else {
        parametre.degeri = dosya.name;
      }

      this.kontrolDeger(parametre, this.gosterilecekParametreler, this.kart);
      this.CD.detectChanges();
    }
  }

  // 🚀 Zaman Aralığı getter/setter
  getZamanAraligiDegeri(parametre: IParametreTumu, ozellik: 'baslangic' | 'bitis'): string {
    if (!parametre.degeri || typeof parametre.degeri !== 'object') {
      parametre.degeri = { baslangic: null, bitis: null };
    }
    return parametre.degeri[ozellik] || '';
  }

  setZamanAraligiDegeri(parametre: IParametreTumu, ozellik: 'baslangic' | 'bitis', deger: string): void {
    if (!parametre.degeri || typeof parametre.degeri !== 'object') {
      parametre.degeri = { baslangic: null, bitis: null };
    }
    parametre.degeri[ozellik] = deger;
    this.kontrolDeger(parametre, this.gosterilecekParametreler, this.kart);
  }

  // 🚀 XYZ Koordinat getter/setter
  getXyzDegeri(parametre: IParametreTumu, eksen: 'x' | 'y' | 'z'): number {
    if (!parametre.degeri || typeof parametre.degeri !== 'object') {
      parametre.degeri = { x: 0, y: 0, z: 0 };
    }
    return parametre.degeri[eksen] || 0;
  }

  setXyzDegeri(parametre: IParametreTumu, eksen: 'x' | 'y' | 'z', deger: number): void {
    if (!parametre.degeri || typeof parametre.degeri !== 'object') {
      parametre.degeri = { x: 0, y: 0, z: 0 };
    }
    parametre.degeri[eksen] = deger;
    this.kontrolDeger(parametre, this.gosterilecekParametreler, this.kart);
  }

  // 🚀 Hızlı arama fonksiyonu - tüm parametreleri, alt parametreleri ve custom parametreleri kapsar
  aramaYap(terim: string): void {
    if (!terim || terim.length < 2) {
      this.aramaSonuclari = this.parametreler || [];
      return;
    }

    const cacheKey = `arama_${terim}`;
    if (this.cache.has(cacheKey)) {
      this.aramaSonuclari = this.cache.get(cacheKey);
      return;
    }

    this.isLoading = true;
    const terimLower = terim.toLowerCase();

    setTimeout(() => {
      const sonuclar = this.recursiveParametreAra(this.parametreler || [], terimLower);
      this.cache.set(cacheKey, sonuclar);
      this.aramaSonuclari = sonuclar;
      this.isLoading = false;
      this.CD.detectChanges();
    }, 100);
  }

  /** Recursive parametre arama - Grup, Sekme, 2liSatır, 3lüSatır alt parametrelerini de arar */
  private recursiveParametreAra(parametreler: IParametreTumu[], terim: string): IParametreTumu[] {
    const sonuclar: IParametreTumu[] = [];
    for (const param of parametreler) {
      if (!this.gorebilirMi(param)) continue;
      // Ana parametrede eşleşme kontrolü
      if (this.parametreEslesiyorMu(param, terim)) {
        sonuclar.push(param);
        continue; // Ana eşleşti, alt parametreleri de dahil oluyor zaten
      }
      // Alt parametrelerde recursive arama
      let altEslesti = false;
      if (param.turu === 'Grup' && Array.isArray(param.degeri)) {
        altEslesti = this.recursiveParametreAra(param.degeri as IParametreTumu[], terim).length > 0;
      } else if (param.turu === 'Sekme' && Array.isArray(param.degeri)) {
        for (const sekme of param.degeri as any[]) {
          if (Array.isArray(sekme?.degeri)) {
            if (this.recursiveParametreAra(sekme.degeri as IParametreTumu[], terim).length > 0) {
              altEslesti = true;
              break;
            }
          }
        }
      } else if (param.turu === '2liSatır' && param.degeri) {
        const d = param.degeri as any;
        if (d.solTaraf && this.parametreEslesiyorMu(d.solTaraf, terim)) altEslesti = true;
        if (d.sagTaraf && this.parametreEslesiyorMu(d.sagTaraf, terim)) altEslesti = true;
      } else if (param.turu === '3lüSatır' && param.degeri) {
        const d = param.degeri as any;
        if (d.solTaraf && this.parametreEslesiyorMu(d.solTaraf, terim)) altEslesti = true;
        if (d.ortaTaraf && this.parametreEslesiyorMu(d.ortaTaraf, terim)) altEslesti = true;
        if (d.sagTaraf && this.parametreEslesiyorMu(d.sagTaraf, terim)) altEslesti = true;
      }
      if (altEslesti) sonuclar.push(param);
    }
    return sonuclar;
  }

  /** Tek parametrede eşleşme kontrolü - başlık, isim, tür, açıklama, ipucu, değer, ayarlar */
  private parametreEslesiyorMu(param: IParametreTumu, terim: string): boolean {
    return !!(
      param.baslik?.toLowerCase().includes(terim) ||
      param.ismi?.toLowerCase().includes(terim) ||
      param.turu?.toLowerCase().includes(terim) ||
      param.altAciklama?.toLowerCase().includes(terim) ||
      param.ipucu?.toLowerCase().includes(terim) ||
      param.yerTutucu?.toLowerCase().includes(terim) ||
      // Değer arama (string ise)
      (typeof param.degeri === 'string' && param.degeri.toLowerCase().includes(terim)) ||
      // Ayarlar içinde arama
      (param.ayarlar?.solSimge?.toLowerCase().includes(terim)) ||
      (param.ayarlar?.format?.toLowerCase().includes(terim)) ||
      // Seçenekler içinde arama (Açılır Kutu, Oylama)
      (param.ayarlar?.secenekler?.some((s: any) =>
        s.label?.toLowerCase().includes(terim) ||
        s.value?.toString().toLowerCase().includes(terim) ||
        s.etiket?.toLowerCase().includes(terim) ||
        s.deger?.toString().toLowerCase().includes(terim)
      )) ||
      // FAPI ayarları arama
      (param.ayarlar?.fapiAyarlar?.vt?.toLowerCase().includes(terim)) ||
      (param.ayarlar?.fapiAyarlar?.yol?.toLowerCase().includes(terim)) ||
      // Kontroller mesajları arama
      (param.kontroller && Object.values(param.kontroller).some((k: any) =>
        k.mesaj?.toLowerCase().includes(terim)
      )) ||
      // Olaylar kodu arama
      (param.ayarlar?.olaylar && Object.values(param.ayarlar.olaylar).some((kod: any) =>
        typeof kod === 'string' && kod.toLowerCase().includes(terim)
      ))
    );
  }

  // 🚀 Kategorileri oluştur
  private kategorileriOlustur(parametreler: IParametreTumu[]): void {
    const turSet = new Set<string>(['tum']);
    parametreler?.forEach(p => {
      if (p.turu) turSet.add(p.turu);
    });
    this.kategoriler = Array.from(turSet);
  }

  // 🚀 Kategori filtreleme
  kategoriyeGoreFiltre(kategori: string): void {
    this.secilenKategori = kategori;

    if (kategori === 'tum') {
      this.filtrelenmisParametreler$.next(this.parametreler || []);
    } else {
      const filtrelenmis = (this.parametreler || []).filter(p => p.turu === kategori);
      this.filtrelenmisParametreler$.next(filtrelenmis);
    }
  }

  // 🚀 Cache temizleme
  cacheTemizle(): void {
    this.cache.clear();
  }

  // 🚀 Arama terimini güncelle
  aramaTerimiGuncelle(terim: string): void {
    this.aramaTerimi = terim;
    this.aramaTerimi$.next(terim);
  }

  // 🚀 Filtrelenmiş parametreleri public yap
  get filtrelenmisParametreler(): IParametreTumu[] {
    return this.filtrelenmisParametreler$.value || [];
  }


  /** trackBy: parametre ismi bazli — DOM tekrar olusturmayi onler */
  trackByParametreIsmi(index: number, itm: IParametreTumu): string {
    return itm?.ismi ?? String(index);
  }

  override ngAfterViewInit(): void {
    this.CD.detectChanges();
    $('.accordion').accordion();
  }

  /** YAP:  Dönüş Türünü Asenkron Çalışması Amacı İle Promise Yada Observable Yap */
  async kontrolDeger111(itm: any): Promise<boolean | any> {
    if (!('kontroller' in itm)) { return true; }

    const $tipi = new Observable<boolean>(observer => {

      const liste: any = itm.kontroller;
      if (!('hatalar' in itm)) { itm.hatalar = []; }
      itm.hatalar = [];

      Object.keys(liste).forEach((key) => {
        const knt = liste[key];

        if (!(knt.fn(this.parametreler, itm))) {
          itm.hatalar.push(knt.mesaj);
        }


      });

      observer.next(itm.hatalar.length > 0 ? false : true);
      observer.unsubscribe();
      this.CD.detectChanges();
    });

    return $tipi.toPromise();
  }







  /** 🚀 Form Serialize Ediliyor - TypeScript Güvenli */
  _serialize(veri: IParametreTumu[]): Record<string, any> {
    const result: Record<string, any> = {};

    veri?.forEach((element) => {
      if (!element?.ismi) return;

      switch (element.turu) {
        case "Grup":
          if (element.degeri && Array.isArray(element.degeri)) {
            result[element.ismi] = this._serialize(element.degeri as IParametreTumu[]);
          }
          break;

        case "Sekme":
          if (element.degeri && Array.isArray(element.degeri)) {
            if (element.ayarlar?.jsonGrup) {
              element.degeri.forEach((sekmeElement: any) => {
                if (sekmeElement?.ismi && Array.isArray(sekmeElement.degeri)) {
                  result[sekmeElement.ismi] = this._serialize(sekmeElement.degeri as IParametreTumu[]);
                }
              });
            } else {
              element.degeri.forEach((sekmeElement: any) => {
                if (Array.isArray(sekmeElement?.degeri)) {
                  Object.assign(result, this._serialize(sekmeElement.degeri as IParametreTumu[]));
                }
              });
            }
          }
          break;

        case "2liSatır":
          if (element.degeri && typeof element.degeri === 'object' && 'solTaraf' in element.degeri) {
            const degeri = element.degeri as any;
            if (degeri.solTaraf?.ismi !== undefined) {
              result[degeri.solTaraf.ismi] = degeri.solTaraf.degeri;
            }
            if (degeri.sagTaraf?.ismi !== undefined) {
              result[degeri.sagTaraf.ismi] = degeri.sagTaraf.degeri;
            }
          }
          break;

        case "3lüSatır":
          if (element.degeri && typeof element.degeri === 'object' && 'solTaraf' in element.degeri) {
            const degeri = element.degeri as any;
            if (degeri.solTaraf?.ismi !== undefined) {
              result[degeri.solTaraf.ismi] = degeri.solTaraf.degeri;
            }
            if (degeri.ortaTaraf?.ismi !== undefined) {
              result[degeri.ortaTaraf.ismi] = degeri.ortaTaraf.degeri;
            }
            if (degeri.sagTaraf?.ismi !== undefined) {
              result[degeri.sagTaraf.ismi] = degeri.sagTaraf.degeri;
            }
          }
          break;

        case "Simgeli Başlık":
          // Simgeli Başlık sadece görsel amaçlı, form verisine dahil edilmez
          // console.log(`Görsel başlık atlandı: ${element.baslik}`);
          break;

        default:
          // Dosya tipleri için özel handling
          if (element.degeri && typeof element.degeri === 'object' && 'dosya' in element.degeri && '_id' in element.degeri) {
            const dosyaDegeri = element.degeri as any;
            result[element.ismi] = dosyaDegeri._id?.toString() || null;
          } else {
            result[element.ismi] = element.degeri;
          }
          break;
      }
    });

    return result;
  }

  toJson(): any {
    return this._serialize(this.parametreler);
  }

  dosyalar(): any[] {
    return parametreAyikla(this.parametreler).dosyalar ?? [];
  }


  toFormData(): FormData {
    const sonuc = parametreAyikla(this.parametreler);

    const formData = new FormData();
    formData.append("req_body", JSON.stringify(sonuc.json));
    for (const d of sonuc.dosyalar) {
      formData.append(d._id, d.dosya, d.dosya.name);
    }

    return formData;
  }


  /** BİLGİ:  Formu resetleme */
  sifirla(form: any[]): void {
    Array.from(form).forEach(element => {

      //@ts-ignore
      if (element.turu == 'aciklama' || element.turu == 'derlemeci' || element.turu == 'Simgeli Başlık') {
        /** NOT:  Aciklama, Derlemeci ve Simgeli Başlığın Değerleri Sıfırlanmıyor
         * Çünkü Onlar Formda Bilgi Verme Amaçlı Kullanılacak
         */
      } else {

        if (element.turu != "Grup") {
          if (element.turu == "XYZ") {
            element.degeri = {}
          } else {
            element.degeri = null;
          }
          element.hatalar = []
        } else {
          this.sifirla(element.degeri)
        }

      }

    })
  }

  tiklandiDeneme(): void {
    console.debug(this._serialize(this.parametreler));
  }


  /** BİLGİ:  Resim Kutusu Bölümü */
  dosyaDialogAc(accept: string, multy: boolean = false, callback: any): void {
    var inputElement = document.createElement("input");
    inputElement.type = "file";
    inputElement.accept = accept; // Note Edge does not support this attribute;
    //@ts-ignore
    // inputElement.capture = "camera";
    if (multy) {
      inputElement.multiple = multy;
    }
    if (typeof callback === "function") {
      inputElement.addEventListener("change", callback);
    }
    inputElement.dispatchEvent(new MouseEvent("click"));
  }








  parametreBul(
    kriter: Partial<IParametre>,
    opsiyon: ParametreBulOpsiyon = {}
  ): IParametre[] {
    const { tamEslestir = false, ozelFiltreler = [] } = opsiyon;

    return this.parametreler.filter(p => {
      // 1️⃣ standart kriter kontrolü
      const kriterKontrol = Object.entries(kriter).every(([key, value]) => {
        const alan = (p as any)[key];

        if (typeof value === "string" && typeof alan === "string") {
          const alanLower = alan.toLowerCase();
          const valueLower = value.toLowerCase();

          return tamEslestir ? alanLower === valueLower : alanLower.includes(valueLower);
        }

        return alan === value;
      });

      if (!kriterKontrol) return false;

      // 2️⃣ tüm özel filtreleri uygula
      for (const f of ozelFiltreler) {
        if (!f(p)) return false;
      }

      return true;
    });
  }

  parametreProxy(parametreler: any[]): any {
    return new Proxy({}, {
      get(_, prop: string) {
        return parametreler.find(p => p.ismi === prop)?.degeri ?? null;
      },
      set(_, prop: string, value) {
        const param = parametreler.find(p => p.ismi === prop);
        if (param) {
          param.degeri = value;
          return true;
        }
        return false; // yoksa hiçbir şey yapma
      }
    });
  }

  // @debounce(500)
  async acilirKutuAra(itm: IAcılırKutuParametre, value: string, parametreler: any) {
    itm.mesgul = true;
    this.CD.detectChanges();
    let istekKayitVarmi = new FikoYTIstekKok({
      metod: 'POST',
      komut: 'FikoYT.sistem.DB.hareketler.aggregate',
      gecikmeliCevap: false,
    })

    let pipe = [];
    try {
      const tsCode = `
      (function (form: any, prm:any, itm: any, deger: any='')  {
          ${itm?.ayarlar?.fapiAyarlar?.pipeFunc}
      })
    `;
      const jsCode = tipAnotasyonlarinSoy(tsCode); // TS → JS
      // console.log(this.parametreler, this.parametreProxy(parametreler).firma, itm, value);
      pipe = runDynamicCode(jsCode, [this.parametreler, this.parametreProxy(parametreler), itm, typeof value === 'string' ? escapeRegex(value) : value]);
    } catch (error) {
      pipe = [];
    }

    const fapiMatch: any = {};
    if (itm?.ayarlar?.fapiAyarlar?.vt) fapiMatch.vt = itm.ayarlar.fapiAyarlar.vt;
    if (itm?.ayarlar?.fapiAyarlar?.yol) fapiMatch.yol = itm.ayarlar.fapiAyarlar.yol;
    istekKayitVarmi.parametreler = {
      aggregate: [
        { $match: fapiMatch },
        ...pipe,
      ]
    };
    await istekKayitVarmi.isteSync();
    if (istekKayitVarmi.cevap?.sonuc) {
      itm.ayarlar.secenekler = istekKayitVarmi.cevap.sonuc;
    } else {
      itm.ayarlar.secenekler = null;
    }
    itm.mesgul = false;
    this.CD.detectChanges();
  }

  /** Genel olay çalıştırıcı */
  async olayiCalistir(olayAdi: string, itm: any, value: any, parametreler: any, sonrasiIslem: boolean = false) {
    try {
      const olayKodu = itm?.ayarlar?.olaylar?.[olayAdi];
      if (!olayKodu) return;

      const tsCode = `
      (function (form: any, prm: any, itm: any, deger: any, kart: any)  {
          ${olayKodu}
      })
    `;
      const jsCode = tipAnotasyonlarinSoy(tsCode);
      runDynamicCode(jsCode, [this.parametreler, this.parametreProxy(parametreler), itm, value, this.kart]);
    } catch (error) {
      console.error(`Olay çalıştırma hatası (${olayAdi}):`, error);
      this.nzMessage?.warning('Olay çalıştırılırken hata oluştu');
    } finally {
      if (sonrasiIslem) {
        kontrolDeger(itm, this.parametreler, this.kart);
        this.CD.detectChanges();
        this.FikoYT.degisti.emit(itm);
      }
    }
  }

  /** Değişti olayı */
  async olayDegisti(itm: any, value: string, parametreler: any) {
    await this.olayiCalistir('degisti', itm, value, parametreler, true);
  }

  /** Seçildi olayı */
  async olaySecildi(itm: any, value: string, parametreler: any) {
    await this.olayiCalistir('secildi', itm, value, parametreler, true);
  }

  /** Seçim silindi olayı */
  async olaySecimSilindi(itm: any, value: string, parametreler: any) {
    await this.olayiCalistir('secimSilindi', itm, value, parametreler, false);
  }

  /** Focus olayı */
  async olayFocus(itm: any, event: any, parametreler: any) {
    await this.olayiCalistir('focus', itm, event, parametreler, false);
  }

  /** Blur olayı */
  async olayBlur(itm: any, event: any, parametreler: any) {
    await this.olayiCalistir('blur', itm, event, parametreler, true);
  }

  /** Tıklanma olayı */
  async olayTiklanma(itm: any, event: any, parametreler: any) {
    await this.olayiCalistir('tiklanma', itm, event, parametreler, false);
  }

  acilirKutuItm(itm: IAcılırKutuParametre, secenek: any) {
    if (!itm?.ayarlar?.secenekItm) {
      if (typeof secenek === 'string') return { value: secenek, label: secenek };
      return { value: secenek?.value ?? secenek?.deger ?? secenek, label: secenek?.label ?? secenek?.etiket ?? secenek?.value ?? secenek?.deger ?? '', simge: secenek?.simge, renk: secenek?.renk, aciklama: secenek?.aciklama, resimUrl: secenek?.resimUrl };
    }
    const tsCode = `
    (function (form: any, itm: any, scnk: any)  {
        ${itm.ayarlar.secenekItm}
    })
  `;
    const jsCode = tipAnotasyonlarinSoy(tsCode);
    return runDynamicCode(jsCode, [this.parametreler, itm, secenek]);
  }


  tiklandiParametreEkle() {
    if (!this.parametreEkleyebilirMi()) {
      this.nzNotification?.error('Yetki Hatası', 'Parametre ekleme yetkiniz bulunmuyor');
      return;
    }
    const yeni: IParametreTumu = {
      turu: 'Yazı',
      ismi: 'parametre_' + Date.now(),
      baslik: 'Yeni Parametre',
      degeri: null,
      ayarlar: { olaylar: {} }
    };
    if (!this.parametreler) this.parametreler = [];
    this.parametreler.push(yeni);
    const idx = this.parametreler.length - 1;
    this.ayarlarAcikSet.add(idx);
    this.parametreler$.next(this.parametreler);
    this.CD.detectChanges();
    this.degisti.emit(this.parametreler);
    this.nzNotification.success("Başarılı", "Yeni parametre eklendi. Ayarları düzenleyin.");
  }

  tiklandiParametreDuzenle(parametre: IParametreTumu, index: number) {
    if (!this.duzenleyebilirMi(parametre)) {
      this.nzNotification?.error('Yetki Hatası', this.getYetkiMesaji(parametre, 'duzenleme') || 'Bu parametreyi düzenleme yetkiniz bulunmuyor');
      return;
    }
    // Inline ayarlar panelini aç
    if (!this.ayarlarAcikSet.has(index)) {
      this.toggleInlineAyarlar(index);
    }
  }

  tiklandiParametreSil(index: number) {
    const parametre = this.parametreler[index];

    // Yetki kontrolü
    if (!this.silebilirMi(parametre)) {
      this.nzNotification?.error('Yetki Hatası', this.getYetkiMesaji(parametre, 'silme') || 'Bu parametreyi silme yetkiniz bulunmuyor');
      return;
    }

    // Onay isteği
    this.nzModalServis.confirm({
      nzTitle: 'Emin misiniz?',
      nzContent: `"${parametre.baslik}" parametresi silinecektir. Bu işlem geri alınamaz.`,
      nzOkText: 'Evet, Sil',
      nzCancelText: 'İptal',
      nzOkDanger: true,
      nzOnOk: () => {
        this.parametreler.splice(index, 1);
        this.nzNotification?.success("Başarılı", `"${parametre.baslik}" parametresi silindi`);

        // 🚀 BehaviorSubject'i güncelle
        this.parametreler$.next(this.parametreler);

        this.CD.detectChanges();
        this.degisti.emit(this.parametreler);
      }
    });
  }

  /** Hızlı ekleme ile parametre eklendi */
  hizliParametreEklendi(parametre: IParametre) {
    if (!this.parametreler) {
      this.parametreler = [];
    }
    this.parametreler.push(parametre as IParametreTumu);
    this.parametreler$.next(this.parametreler);
    this.CD.detectChanges();
    this.degisti.emit(this.parametreler);
  }

  /** Hazır set toplu eklendi */
  hazirSetEklendi(parametreler: IParametre[]) {
    if (!this.parametreler) {
      this.parametreler = [];
    }
    const mevcutIsimler = this.parametreler.map(p => p.ismi);
    for (const p of parametreler) {
      if (!mevcutIsimler.includes(p.ismi)) {
        this.parametreler.push(p as IParametreTumu);
        mevcutIsimler.push(p.ismi);
      }
    }
    this.parametreler$.next(this.parametreler);
    this.CD.detectChanges();
    this.degisti.emit(this.parametreler);
  }

  /** Mevcut parametre isimlerini al */
  getMevcutIsimler(): string[] {
    return (this.parametreler || []).map(p => p.ismi);
  }

  /** Inline ayarları aç/kapat */
  toggleInlineAyarlar(index: number) {
    if (this.ayarlarAcikSet.has(index)) {
      this.ayarlarAcikSet.delete(index);
    } else {
      const itm = this.parametreler[index];
      if (!itm.ayarlar) itm.ayarlar = {};
      if (!itm.ayarlar.olaylar) itm.ayarlar.olaylar = {};
      this.ayarlarAcikSet.add(index);
    }
  }

  /** Seçenek ekleme (Açılır Kutu, Oylama vb.) */
  inlineSecenekEkle(itm: any, turSecenegi: 'acilirKutu' | 'oylama' = 'acilirKutu') {
    if (!itm.ayarlar) itm.ayarlar = {};
    if (!itm.ayarlar.secenekler) itm.ayarlar.secenekler = [];
    if (turSecenegi === 'oylama') {
      itm.ayarlar.secenekler.push({ etiket: '', deger: '', renk: '#1890ff' });
    } else {
      const yeniSecenek: any = { label: '', value: '' };
      if (itm.ayarlar.itemSablon?.aktif) {
        if (itm.ayarlar.itemSablon.simgeGoster) yeniSecenek.simge = '';
        if (itm.ayarlar.itemSablon.renkGoster) yeniSecenek.renk = '#1890ff';
        if (itm.ayarlar.itemSablon.aciklamaGoster) yeniSecenek.aciklama = '';
        if (itm.ayarlar.itemSablon.resimGoster) yeniSecenek.resimUrl = '';
      }
      itm.ayarlar.secenekler.push(yeniSecenek);
    }
    this.inlineAyarDegisti();
  }

  /** Seçenek silme */
  inlineSecenekSil(itm: any, idx: number) {
    itm.ayarlar?.secenekler?.splice(idx, 1);
    this.inlineAyarDegisti();
  }

  _hizliEkleAcik = false;
  _hizliEkleItm: any = null;

  ayarModalAcik = false;
  ayarModalItm: any = null;
  ayarModalIndex = -1;

  ayarModalAc(itm: any) {
    if (!itm) return;
    if (!itm?.ayarlar) itm.ayarlar = {};
    if (!itm.ayarlar?.olaylar) itm.ayarlar.olaylar = {};
    this.ayarModalItm = itm;
    this.ayarModalIndex = this.parametreler?.indexOf(itm) ?? -1;
    this.ayarModalAcik = true;
    this.CD.detectChanges();
  }

  ayarModalKapat() {
    this.inlineAyarDegisti();
    this.ayarModalAcik = false;
    this.ayarModalItm = null;
    this.ayarModalIndex = -1;
    this.CD.detectChanges();
  }
  private _hizliEkleRef: ComponentRef<any> = null;
  @ViewChild('hizliEkleKonteynir', { read: ViewContainerRef }) hizliEkleKonteynir: ViewContainerRef;

  async acilirKutuHizliEkle(itm: any) {
    const baslik = itm.ayarlar?.hizliEkle || 'Kayıt';
    const setId = itm.ayarlar?.hizliEkleSetId;
    let bulunanSet: any = null;
    // Önce dropdown ayarlarındaki gömülü setlere bak
    const gomuluSetler = itm.ayarlar?.hizliEkleKaynakSetleri;
    const tumSetler = [...(gomuluSetler || []), ...(this.hizliEkleKaynakSetleri || [])];
    if (setId && tumSetler.length) {
      for (const g of tumSetler) {
        if (g.id === setId) { bulunanSet = g; break; }
        if (g.altGruplar) for (const s of g.altGruplar) { if (s.id === setId) { bulunanSet = s; break; } }
        if (bulunanSet) break;
      }
    }
    const fapi = itm.ayarlar?.fapiAyarlar;
    const kart = {
      _id: Date.now().toString(36),
      vt: fapi?.vt || 'FikoYT.sistem.hareketler',
      yol: fapi?.yol || 'FikoYT.sistem.hareketler.kalem',
      turu: 'KART',
      gizlilik: 'Herkese Açık',
      uygulama: fapi?.uygulama ? [fapi.uygulama] : [],
      kisiler: [],
      veri: {
        solSimge: fapi?.solSimge || { baslik: baslik.charAt(0), renkArka: '#1890ff', renkYazi: '#fff' },
        kurdela: fapi?.kurdela || undefined,
        icerikler: [{ _id: Date.now().toString(36), html: '', aciklama: '', sifre: null }],
      },
    };
    let setleri: any[];
    if (bulunanSet) {
      setleri = [JSON.parse(JSON.stringify(bulunanSet))];
    } else {
      const alanlar = itm.ayarlar?.hizliEkleAlanlar;
      let setParametreler: any[] = [];
      if (alanlar?.length) setParametreler = alanlar.map((a: any) => ({ turu: 'Yazı', ismi: a.ismi, baslik: a.baslik, ayarlar: a.zorunlu ? { zorunlu: true } : {}, kontroller: [], hatalar: [], uyarilar: [] }));
      setleri = [{ id: 'hizli.' + baslik.toLowerCase(), baslik, simge: itm.ayarlar?.solSimge || '📋', kaynak: 'kod', parametreler: setParametreler }];
    }
    this._hizliEkleItm = itm;
    this._hizliEkleAcik = true;
    this.CD.detectChanges();
    await new Promise(r => setTimeout(r, 50));
    if (!this.hizliEkleKonteynir) return;
    const { PaylasKutusuComponent } = await import('../paylasim-sistemi/paylas-kutusu/paylas-kutusu.component');
    this.hizliEkleKonteynir.clear();
    this._hizliEkleRef = this.hizliEkleKonteynir.createComponent(PaylasKutusuComponent);
    this._hizliEkleRef.instance.kart = kart;
    this._hizliEkleRef.instance.parametreSetleri = setleri;
    this._hizliEkleRef.instance.parametreSetiZorunlu = true;
    this._hizliEkleRef.instance.taslakDevreDisi = true;
    this._hizliEkleRef.instance.paylasimYapildi?.subscribe(() => this.hizliEklePaylasimYapildi());
    this._hizliEkleRef.changeDetectorRef.detectChanges();
  }

  hizliEklePaylasimYapildi() {
    const itm = this._hizliEkleItm;
    this.hizliEkleIptal();
    if (itm?.ayarlar?.fapiAyarlar) this.acilirKutuAra(itm, '', this.parametreler);
    this.nzNotification.success('Başarılı', 'Kayıt eklendi');
  }

  hizliEkleIptal() {
    this._hizliEkleAcik = false;
    this._hizliEkleItm = null;
    this._hizliEkleRef?.destroy();
    this._hizliEkleRef = null;
    this.hizliEkleKonteynir?.clear();
    this.CD.detectChanges();
  }

  private _onizlemeCache = new Map<string, any[]>();
  private _onizlemeYukleniyor = new Set<string>();

  acilirKutuOnizlemeVeri(itm: any): any[] | null {
    const deger = itm.degeri;
    if (!deger) return null;
    if (this._onizlemeCache.has(deger)) return this._onizlemeCache.get(deger);
    if (this._onizlemeYukleniyor.has(deger)) return null;
    this._onizlemeYukleniyor.add(deger);
    const vt = itm.ayarlar?.fapiAyarlar?.vt;
    const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.hareketler.aggregate', gecikmeliCevap: false });
    istek.parametreler = { aggregate: [{ $match: { _id: deger, vt } }, { $limit: 1 }] };
    istek.isteSync().then(() => {
      const kayit = istek.cevap?.sonuc?.[0];
      let alanlar: any[] = [];
      if (kayit) {
        const params = kayit.veri?.icerikler?.[0]?.ek?.parametreler;
        if (params?.length) {
          alanlar = params.filter((p: any) => p.degeri != null && p.degeri !== '').map((p: any) => ({ ismi: p.ismi, baslik: p.baslik || p.ismi, degeri: typeof p.degeri === 'object' ? JSON.stringify(p.degeri) : String(p.degeri) }));
        } else {
          const skip = ['_id', 'vt', 'yol', 'veri', 'icerikler', 'silindi', 'aktif', 'olusturulmaTarihi', 'guncellenmeTarihi', 'uygulama', 'turu', 'gizlilik', 'kisiler'];
          Object.keys(kayit).filter(k => !skip.includes(k) && kayit[k] != null && kayit[k] !== '').forEach(k => alanlar.push({ ismi: k, baslik: k, degeri: String(kayit[k]) }));
        }
      }
      this._onizlemeCache.set(deger, alanlar);
      this._onizlemeYukleniyor.delete(deger);
      this.CD.detectChanges();
    }).catch(() => {
      this._onizlemeYukleniyor.delete(deger);
    });
    return null;
  }

  /** Item şablon toggle */
  itemSablonToggle(itm: any) {
    if (!itm.ayarlar) itm.ayarlar = {};
    if (itm.ayarlar.itemSablon?.aktif) {
      itm.ayarlar.itemSablon.aktif = false;
    } else {
      itm.ayarlar.itemSablon = { aktif: true, simgeGoster: true, renkGoster: true, aciklamaGoster: false, resimGoster: false };
    }
    this.inlineAyarDegisti();
  }

  /** Açılır kutu seçili seçenek bilgisini bul (gösterim modu) */
  acilirKutuSeciliLabel(itm: any, mapped: any): string {
    return mapped?.label || '';
  }

  xyzBaslat(itm: any) {
    if (!itm.degeri) itm.degeri = { x: 0, y: 0, z: 0 };
  }

  acilirKutuSeciliSecenek(itm: any): any {
    if (!itm.degeri || !itm.ayarlar?.secenekler) return null;
    for (const s of itm.ayarlar.secenekler) {
      if (typeof s === 'string') {
        if (s === itm.degeri) return { label: s };
      } else {
        const mapped = itm.ayarlar?.secenekItm ? this.acilirKutuItm(itm, s) : null;
        const val = mapped?.value ?? s.value ?? s.deger;
        if (val === itm.degeri) return { ...s, label: mapped?.label ?? s.label ?? s.etiket, simge: s.simge ?? mapped?.simge, renk: s.renk ?? mapped?.renk, aciklama: s.aciklama ?? mapped?.aciklama, resimUrl: s.resimUrl ?? mapped?.resimUrl };
      }
    }
    return { label: itm.degeri };
  }

  /** Açılır kutu bağlantı — modal veya yeni sekme */
  acilirKutuBaglantiAc(itm: any, degerOverride?: any) {
    const deger = degerOverride ?? itm.degeri;
    if (!deger) return;
    if (itm.ayarlar?.baglantiModu === 'modal') {
      this.acilirKutuModalAc(itm, deger);
      return;
    }
    const sablon = itm.ayarlar?.baglantiSablonu;
    if (!sablon) return;
    if (sablon.includes('return ')) {
      try {
        const secili = itm.ayarlar?.secenekler?.find((s: any) => {
          const mapped = itm.ayarlar?.secenekItm ? this.acilirKutuItm(itm, s) : null;
          return (mapped?.value ?? s?.value ?? s?.deger ?? s) === deger;
        });
        const tsCode = `(function(deger, secenek) { ${sablon} })`;
        const jsCode = tipAnotasyonlarinSoy(tsCode);
        const url = runDynamicCode(jsCode, [deger, secili]);
        if (url) window.open(url, '_blank');
      } catch (e) { console.error('Bağlantı hatası:', e); }
    } else {
      window.open(sablon.replace(/\{deger\}/g, deger), '_blank');
    }
  }

  /** Açılır kutu — seçili kaydı modal ile göster */
  private async acilirKutuModalAc(itm: any, degerOverride?: any) {
    const vt = itm.ayarlar?.fapiAyarlar?.vt;
    const hedefDeger = degerOverride ?? itm.degeri;
    if (!vt || !hedefDeger) return;
    const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.hareketler.aggregate', gecikmeliCevap: false });
    istek.parametreler = { aggregate: [{ $match: { _id: hedefDeger, vt } }, { $limit: 1 }] };
    await istek.isteSync();
    const kayit = istek.cevap?.sonuc?.[0];
    if (!kayit) { this.nzNotification.warning('Bulunamadı', 'Kayıt bulunamadı'); return; }

    const v = kayit.veri || {};
    const icerikHtml = kayit.icerikler?.[0]?.html || '';
    const dosyalar: any[] = kayit.dosyalar || [];
    const tarih = kayit.tarih ? new Date(kayit.tarih).toLocaleString('tr-TR') : '';

    let baslik = v.konu || v.ismi || v.baslik || itm.baslik || 'Detay';
    let html = '';

    // Mail üst bilgi
    if (v.kimden || v.kime) {
      const gIsim = MailUI.gonderenIsim(v.kimden);
      const gAdres = MailUI.gonderenAdres(v.kimden);
      const renk = MailUI.avatarRenk(v.kimden);
      const harf = MailUI.avatarHarf(v.kimden);

      html += `<div style="display:flex;gap:12px;align-items:flex-start;padding:14px 16px;border-radius:10px;background:var(--py-arkaplan-ikincil,#f7f8fa);margin-bottom:14px;">`;
      html += `<div style="width:40px;height:40px;border-radius:50%;background:${renk};color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:16px;flex-shrink:0;">${harf}</div>`;
      html += `<div style="flex:1;min-width:0;font-size:13px;line-height:1.7;">`;
      html += `<div style="font-weight:600;font-size:14px;">${gIsim}</div>`;
      if (gAdres && gAdres !== gIsim) html += `<div style="color:#8c8c8c;">${gAdres}</div>`;
      if (v.kime) html += `<div style="color:#8c8c8c;">Alıcı: ${v.kime}</div>`;
      if (tarih) html += `<div style="color:#8c8c8c;">${tarih}</div>`;
      html += `</div></div>`;
    }

    // Ekler — indirilebilir chip'ler
    if (dosyalar.length) {
      html += `<div style="display:flex;flex-wrap:wrap;gap:6px;padding:0 0 12px;">`;
      dosyalar.forEach((d: any) => {
        const ismi = MailUI.mimeDecodeIsim(d.ismi || d.dosyaAdi || 'Dosya');
        const boyut = d.boyut ? MailUI.dosyaBoyutFormatla(d.boyut) : '';
        const dosyaId = d.dosyaId || d._id || '';
        const url = d.url || (dosyaId ? MailUI.ekIndirUrl(dosyaId) : '');
        const href = url ? `href="${url}" target="_blank"` : '';
        html += `<a ${href} style="display:inline-flex;align-items:center;gap:5px;padding:6px 12px;background:#f0f5ff;border:1px solid #d6e4ff;border-radius:10px;font-size:12px;color:#1890ff;text-decoration:none;cursor:pointer;transition:background .2s;"`;
        html += ` onmouseover="this.style.background='#d6e4ff'" onmouseout="this.style.background='#f0f5ff'">`;
        html += `⬇ ${ismi}`;
        if (boyut) html += `<span style="color:#8c8c8c;font-size:11px;">(${boyut})</span>`;
        html += `</a>`;
      });
      html += `</div>`;
    }

    // Mail içeriği
    if (icerikHtml) {
      html += `<div style="max-height:400px;overflow:auto;padding:12px 16px;border:1px solid var(--py-kenarlık,#e8e8e8);border-radius:8px;background:var(--py-arkaplan,#fff);font-size:14px;line-height:1.7;">${icerikHtml}</div>`;
    }

    this.nzModalServis.create({
      nzTitle: baslik,
      nzContent: html,
      nzWidth: 700,
      nzFooter: null,
      nzBodyStyle: { 'max-height': '75vh', overflow: 'auto' },
    });
  }

  /** FAPI Ayarları toggle */
  inlineFapiToggle(itm: any) {
    if (!itm.ayarlar) itm.ayarlar = {};
    if (itm.ayarlar.fapiAyarlar) {
      delete itm.ayarlar.fapiAyarlar;
    } else {
      itm.ayarlar.fapiAyarlar = { vt: '', yol: '', pipeFunc: '' };
    }
    this.inlineAyarDegisti();
  }

  /** Kontrol kuralı ekle */
  inlineKontrolEkle(itm: any) {
    if (!itm.kontroller) itm.kontroller = {};
    const key = 'kural_' + Date.now();
    itm.kontroller[key] = { fn: '', mesaj: '', renk: 'red' };
    this.inlineAyarDegisti();
  }

  /** Kontrol kuralı sil */
  inlineKontrolSil(itm: any, key: string) {
    delete itm.kontroller[key];
    this.inlineAyarDegisti();
  }

  /** Kontrol kuralı anahtarlarını al */
  kontrolAnahtarlari(itm: any): string[] {
    return itm.kontroller ? Object.keys(itm.kontroller) : [];
  }

  /** Inline ayarlar açık mı */
  ayarlarAcikMi(index: number): boolean {
    return this.ayarlarAcikSet.has(index);
  }

  // ═══════════════════════════════════════════════════════
  // 🔧 IDE-Benzeri Gelişmiş Özellikler
  // ═══════════════════════════════════════════════════════

  /** JSON görünüm açık olan parametrelerin indeksleri */
  jsonGorunumAcikSet = new Set<number>();

  /** Tüm parametre türleri listesi */
  tumTurler: ITurParametre[] = [
    'Yazı', 'Yazı Alanı', 'Parola', 'Sayı', 'Doğruluk', 'Onay', 'Anahtar',
    'Tarih', 'Tarih Saat', 'Ay', 'Hafta', 'Saat',
    'Resim', 'Video', 'Dosya', 'Ses', 'XYZ',
    'Grup', 'Para', 'Liste', 'Tablo',
    'KareKod', 'BarKod', '2liSatır', '3lüSatır', 'Sekme', 'Açılır Kutu', 'Simgeli Başlık',
    'Renk', 'Oylama', 'İlerleme Çubuğu', 'Zaman Aralığı', 'Konum',
    'İmza', 'RichText', 'Kod', 'Dosya Yükleme', 'Dinamik Bileşen', 'Dosya Kutusu'
  ];

  /** Parametre türünü inline olarak değiştir */
  turDegistir(itm: IParametreTumu, yeniTur: string) {
    const eskiTur = itm.turu;
    (itm as any).turu = yeniTur;
    // Tür değiştiğinde uyumsuz değeri sıfırla
    if (this.turDegistigindeVarsayilanDegerGerekli(eskiTur, yeniTur)) {
      itm.degeri = this.turuneGoreVarsayilanDeger(yeniTur);
    }
    this.inlineAyarDegisti();
    this.nzNotification.info('Tür Değişti', `"${itm.baslik}" türü ${eskiTur} → ${yeniTur} olarak değiştirildi`);
  }

  /** Tür değiştiğinde varsayılan değer gerekli mi? */
  private turDegistigindeVarsayilanDegerGerekli(eski: string, yeni: string): boolean {
    const yapiTurleri: Record<string, string> = {
      'Yazı': 'string', 'Yazı Alanı': 'string', 'Parola': 'string', 'Renk': 'string',
      'Sayı': 'number', 'Para': 'number',
      'Onay': 'boolean', 'Anahtar': 'boolean', 'Doğruluk': 'string',
      'Tarih': 'date', 'Tarih Saat': 'date', 'Saat': 'string', 'Ay': 'string', 'Hafta': 'string',
      'XYZ': 'xyz', 'Konum': 'konum', 'Zaman Aralığı': 'zamanAraligi',
      'Grup': 'array', 'Sekme': 'array', '2liSatır': 'object', '3lüSatır': 'object',
      'Dinamik Bileşen': 'any',
      'Dosya Kutusu': 'any',
    };
    return (yapiTurleri[eski] || 'any') !== (yapiTurleri[yeni] || 'any');
  }

  /** Türe göre varsayılan değer */
  private turuneGoreVarsayilanDeger(tur: string): any {
    switch (tur) {
      case 'Sayı': case 'Para': return 0;
      case 'Onay': case 'Anahtar': return false;
      case 'Doğruluk': return 'Bilinmiyor';
      case 'XYZ': return { x: 0, y: 0, z: 0 };
      case 'Konum': return { enlem: null, boylam: null };
      case 'Zaman Aralığı': return { baslangic: null, bitis: null };
      case 'Grup': case 'Sekme': return [];
      case '2liSatır': return { solTaraf: { turu: 'Yazı', ismi: 'sol', baslik: 'Sol', degeri: null }, sagTaraf: { turu: 'Yazı', ismi: 'sag', baslik: 'Sağ', degeri: null } };
      case '3lüSatır': return { solTaraf: { turu: 'Yazı', ismi: 'sol', baslik: 'Sol', degeri: null }, ortaTaraf: { turu: 'Yazı', ismi: 'orta', baslik: 'Orta', degeri: null }, sagTaraf: { turu: 'Yazı', ismi: 'sag', baslik: 'Sağ', degeri: null } };
      case 'Dinamik Bileşen': return null;
      case 'Dosya Kutusu': return { dosyalar: [] };
      case 'İlerleme Çubuğu': return 0;
      case 'Renk': return '#1890ff';
      default: return null;
    }
  }

  /** JSON görünüm toggle */
  jsonGorunumToggle(index: number) {
    if (this.jsonGorunumAcikSet.has(index)) {
      this.jsonGorunumAcikSet.delete(index);
    } else {
      this.jsonGorunumAcikSet.add(index);
    }
  }

  jsonGorunumAcikMi(index: number): boolean {
    return this.jsonGorunumAcikSet.has(index);
  }

  /** Parametreyi JSON string olarak al */
  getParametreJson(itm: IParametreTumu): string {
    try {
      const kopyalanan = { ...itm };
      delete (kopyalanan as any).hatalar;
      delete (kopyalanan as any).uyarilar;
      delete (kopyalanan as any).mesgul;
      return JSON.stringify(kopyalanan, null, 2);
    } catch (e) { console.warn('[Parametre] JSON serialize hatası:', e); return '{}'; }
  }

  /** JSON'dan parametreyi güncelle */
  jsonDanGuncelle(itm: IParametreTumu, json: string, index: number) {
    try {
      const parsed = JSON.parse(json);
      Object.assign(itm, parsed);
      this.inlineAyarDegisti();
      this.nzNotification.success('JSON', 'Parametre JSON\'dan güncellendi');
    } catch (e) {
      this.nzNotification.error('JSON Hatası', 'Geçersiz JSON formatı');
    }
  }

  /** Tek parametreyi JSON olarak dışa aktar */
  parametreDisaAktarTekil(itm: IParametreTumu) {
    const json = this.getParametreJson(itm);
    const blob = new Blob([json], { type: 'application/json' });
    this.dosyaIndir(blob, `${itm.ismi || 'parametre'}.json`);
    this.nzNotification.success('Dışa Aktarıldı', `"${itm.baslik}" parametresi dışa aktarıldı`);
  }

  /** Tüm parametreleri JSON olarak dışa aktar */
  tumParametreleriDisaAktar() {
    const json = JSON.stringify(this.parametreler, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    this.dosyaIndir(blob, 'tum_parametreler.json');
    this.nzNotification.success('Dışa Aktarıldı', 'Tüm parametreler dışa aktarıldı');
  }

  /** JSON dosyasından parametre(ler) içe aktar */
  parametreIceriAktar() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (event: any) => {
      const file = event.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e: any) => {
        try {
          const parsed = JSON.parse(e.target.result);
          if (Array.isArray(parsed)) {
            // Dizi ise toplu ekle
            for (const p of parsed) {
              if (p.turu && p.ismi) {
                this.parametreler.push(p as IParametreTumu);
              }
            }
            this.nzNotification.success('İçe Aktarıldı', `${parsed.length} parametre içe aktarıldı`);
          } else if (parsed.turu && parsed.ismi) {
            // Tekil parametre
            this.parametreler.push(parsed as IParametreTumu);
            this.nzNotification.success('İçe Aktarıldı', `"${parsed.baslik}" parametresi içe aktarıldı`);
          } else {
            this.nzNotification.error('Hata', 'Geçersiz parametre formatı');
            return;
          }
          this.parametreler$.next(this.parametreler);
          this.CD.detectChanges();
          this.degisti.emit(this.parametreler);
        } catch {
          this.nzNotification.error('Hata', 'JSON dosyası okunamadı');
        }
      };
      reader.readAsText(file);
    };
    input.click();
  }

  /** Olay kodunu test et (simülasyon) */
  kodTest(itm: IParametreTumu, olayAdi: string) {
    const olayKodu = itm?.ayarlar?.olaylar?.[olayAdi];
    if (!olayKodu) {
      this.nzNotification.warning('Test', 'Bu olay için kod tanımlanmamış');
      return;
    }
    try {
      const tsCode = `(function (form: any, prm: any, itm: any, deger: any = '') { ${olayKodu} })`;
      const jsCode = tipAnotasyonlarinSoy(tsCode);
      const sonuc = runDynamicCode(jsCode, [this.parametreler, this.parametreProxy(this.parametreler), itm, itm.degeri]);
      this.nzNotification.success('Test Başarılı', `"${olayAdi}" olayı hatasız çalıştı. Dönüş: ${JSON.stringify(sonuc) ?? 'void'}`);
    } catch (err: any) {
      this.nzNotification.error('Test Hatası', `"${olayAdi}" olayında hata: ${err.message}`);
    }
    this.CD.detectChanges();
  }

  /** Kontrol kuralını test et */
  kontrolTest(itm: IParametreTumu, key: string) {
    const kural = itm.kontroller?.[key];
    if (!kural?.fn) {
      this.nzNotification.warning('Test', 'Bu kural için fonksiyon tanımlanmamış');
      return;
    }
    try {
      const tsCode = `(function (form: any, itm: any) { ${kural.fn} })`;
      const jsCode = tipAnotasyonlarinSoy(tsCode);
      const sonuc = runDynamicCode(jsCode, [this.parametreler, itm]);
      if (sonuc === false || sonuc === undefined) {
        this.nzNotification.warning('Kural Sonucu', `Kural BAŞARISIZ - Hata mesajı: "${kural.mesaj}"`);
      } else {
        this.nzNotification.success('Kural Sonucu', 'Kural GEÇTİ - Doğrulama başarılı');
      }
    } catch (err: any) {
      this.nzNotification.error('Kural Hatası', `Kural kodunda hata: ${err.message}`);
    }
    this.CD.detectChanges();
  }

  /** Tüm ayarları aç */
  tumAyarlariAc() {
    this.parametreler?.forEach((_, i) => {
      const itm = this.parametreler[i];
      if (!itm.ayarlar) itm.ayarlar = {};
      if (!itm.ayarlar.olaylar) itm.ayarlar.olaylar = {};
      this.ayarlarAcikSet.add(i);
    });
    this.CD.detectChanges();
  }

  /** Tüm ayarları kapat */
  tumAyarlariKapat() {
    this.ayarlarAcikSet.clear();
    this.jsonGorunumAcikSet.clear();
    this.CD.detectChanges();
  }

  /** Bağımlılık ekle */
  bagimlilikEkle(itm: IParametreTumu) {
    if (!itm.ayarlar) itm.ayarlar = {};
    if (!itm.ayarlar.bagimliliklar) itm.ayarlar.bagimliliklar = [];
    itm.ayarlar.bagimliliklar.push({
      hedefParametre: '',
      kosul: 'esittir',
      deger: '',
      aksiyon: 'gorunur'
    });
    this.inlineAyarDegisti();
  }

  /** Bağımlılık sil */
  bagimlilikSil(itm: IParametreTumu, idx: number) {
    itm.ayarlar?.bagimliliklar?.splice(idx, 1);
    this.inlineAyarDegisti();
  }

  /** Formül ekle */
  formulEkle(itm: IParametreTumu) {
    if (!itm.ayarlar) itm.ayarlar = {};
    itm.ayarlar.formul = itm.ayarlar.formul || '';
    this.inlineAyarDegisti();
  }

  /** Formülü değerlendir */
  formulDegerlendir(itm: IParametreTumu) {
    if (!itm.ayarlar?.formul) return;
    try {
      const tsCode = `(function (form: any, prm: any) { ${itm.ayarlar.formul} })`;
      const jsCode = tipAnotasyonlarinSoy(tsCode);
      const sonuc = runDynamicCode(jsCode, [this.parametreler, this.parametreProxy(this.parametreler)]);
      itm.degeri = sonuc;
      this.nzNotification.success('Formül', `Sonuç: ${sonuc}`);
      this.CD.detectChanges();
      this.degisti.emit(this.parametreler);
    } catch (err: any) {
      this.nzNotification.error('Formül Hatası', err.message);
    }
  }

  /** Parametreyi panoya kopyala (JSON olarak) */
  parametreyiPanoyaKopyala(itm: IParametreTumu) {
    const json = this.getParametreJson(itm);
    navigator.clipboard.writeText(json).then(() => {
      this.nzNotification.success('Kopyalandı', 'Parametre JSON olarak panoya kopyalandı');
    }).catch(() => {
      this.nzNotification.error('Hata', 'Panoya kopyalama başarısız');
    });
  }

  /** Panodan parametre yapıştır */
  panodanParametreYapistir() {
    navigator.clipboard.readText().then(text => {
      try {
        const parsed = JSON.parse(text);
        if (parsed.turu && parsed.ismi) {
          parsed.ismi = parsed.ismi + '_yapistir_' + Date.now();
          if (!this.parametreler) this.parametreler = [];
          this.parametreler.push(parsed as IParametreTumu);
          this.parametreler$.next(this.parametreler);
          this.CD.detectChanges();
          this.degisti.emit(this.parametreler);
          this.nzNotification.success('Yapıştırıldı', `"${parsed.baslik}" parametresi yapıştırıldı`);
        } else {
          this.nzNotification.error('Hata', 'Panodaki veri geçerli bir parametre değil');
        }
      } catch (e) {
        console.warn('[Parametre] Pano JSON parse hatası:', e);
        this.nzNotification.error('Hata', 'Panodaki veri geçerli JSON değil');
      }
    }).catch(() => {
      this.nzNotification.error('Hata', 'Pano okunamadı');
    });
  }

  /** Mevcut parametre isimlerini al (bağımlılık seçimi için) */
  getParametreIsimleri(): { ismi: string; baslik: string }[] {
    return (this.parametreler || []).map(p => ({ ismi: p.ismi, baslik: p.baslik || p.ismi }));
  }

  /** Gelişmiş filtre kriterleri */
  gelismisFiltreler: {
    turler?: string[];
    zorunlu?: boolean | null;
    sadeceOkunur?: boolean | null;
    hataliMi?: boolean | null;
    degerliMi?: boolean | null;
    aramaAlanlari?: string[];
  } = {};

  /** Gelişmiş filtreleme uygula - tüm parametreleri, alt parametreleri ve custom parametreleri kapsar */
  gelismisFiltreUygula() {
    let sonuclar = this.parametreler || [];

    // Tür filtresi
    if (this.gelismisFiltreler.turler?.length > 0) {
      sonuclar = sonuclar.filter(p => this.gelismisFiltreler.turler.includes(p.turu));
    }

    // Zorunlu filtresi
    if (this.gelismisFiltreler.zorunlu === true) {
      sonuclar = sonuclar.filter(p => p.ayarlar?.zorunlu === true);
    } else if (this.gelismisFiltreler.zorunlu === false) {
      sonuclar = sonuclar.filter(p => !p.ayarlar?.zorunlu);
    }

    // Salt okunur filtresi
    if (this.gelismisFiltreler.sadeceOkunur === true) {
      sonuclar = sonuclar.filter(p => p.ayarlar?.sadeceOkunur === true);
    } else if (this.gelismisFiltreler.sadeceOkunur === false) {
      sonuclar = sonuclar.filter(p => !p.ayarlar?.sadeceOkunur);
    }

    // Hatalı filtresi
    if (this.gelismisFiltreler.hataliMi === true) {
      sonuclar = sonuclar.filter(p => p.hatalar?.length > 0);
    } else if (this.gelismisFiltreler.hataliMi === false) {
      sonuclar = sonuclar.filter(p => !p.hatalar?.length);
    }

    // Değer dolu/boş filtresi
    if (this.gelismisFiltreler.degerliMi === true) {
      sonuclar = sonuclar.filter(p => p.degeri != null && p.degeri !== '');
    } else if (this.gelismisFiltreler.degerliMi === false) {
      sonuclar = sonuclar.filter(p => p.degeri == null || p.degeri === '');
    }

    // Metin araması (recursive)
    if (this.aramaTerimi && this.aramaTerimi.length >= 2) {
      const terimLower = this.aramaTerimi.toLowerCase();
      sonuclar = this.recursiveParametreAra(sonuclar, terimLower);
    }

    this.aramaSonuclari = sonuclar;
    this.filtrelenmisParametreler$.next(sonuclar);
    this.CD.detectChanges();
  }

  /** Gelişmiş filtreleri sıfırla */
  gelismisFiltreleriSifirla() {
    this.gelismisFiltreler = {};
    this.aramaTerimi = '';
    this.secilenKategori = 'tum';
    this.aramaSonuclari = this.parametreler || [];
    this.filtrelenmisParametreler$.next(this.parametreler || []);
    this.cache.clear();
    this.CD.detectChanges();
  }

  /** Parametreyi kopyala */
  parametreKopyala(index: number) {
    const kaynak = this.parametreler[index];
    if (!kaynak) return;
    const kopya: IParametreTumu = JSON.parse(JSON.stringify(kaynak));
    kopya.ismi = kopya.ismi + '_kopya';
    kopya.baslik = kopya.baslik + ' (Kopya)';
    if (!this.parametreler) this.parametreler = [];
    this.parametreler.splice(index + 1, 0, kopya);
    this.parametreler$.next(this.parametreler);
    this.CD.detectChanges();
    this.degisti.emit(this.parametreler);
  }

  /** Parametre sırasını değiştir */
  parametreTasi(index: number, yon: 'yukari' | 'asagi') {
    const hedef = yon === 'yukari' ? index - 1 : index + 1;
    if (hedef < 0 || hedef >= this.parametreler.length) return;
    const temp = this.parametreler[index];
    this.parametreler[index] = this.parametreler[hedef];
    this.parametreler[hedef] = temp;
    // Açık ayarlar indekslerini güncelle
    const yeniSet = new Set<number>();
    this.ayarlarAcikSet.forEach(i => {
      if (i === index) yeniSet.add(hedef);
      else if (i === hedef) yeniSet.add(index);
      else yeniSet.add(i);
    });
    this.ayarlarAcikSet = yeniSet;
    this.parametreler$.next(this.parametreler);
    this.CD.detectChanges();
    this.degisti.emit(this.parametreler);
  }

  /** Inline ayarlar değiştiğinde emit */
  inlineAyarDegisti() {
    this.undoKaydet();
    this.parametreler$.next(this.parametreler);
    this.degisti.emit(this.parametreler);
  }

  /** CDK DragDrop - parametre sırasını değiştir */
  parametreSurukBirakti(event: CdkDragDrop<IParametreTumu[]>) {
    if (event.previousIndex === event.currentIndex) return;
    moveItemInArray(this.parametreler, event.previousIndex, event.currentIndex);
    // Açık ayarlar indekslerini güncelle
    const yeniSet = new Set<number>();
    this.ayarlarAcikSet.forEach(idx => {
      if (idx === event.previousIndex) {
        yeniSet.add(event.currentIndex);
      } else if (event.previousIndex < event.currentIndex) {
        yeniSet.add(idx >= event.previousIndex && idx <= event.currentIndex ? idx - 1 : idx);
      } else {
        yeniSet.add(idx <= event.previousIndex && idx >= event.currentIndex ? idx + 1 : idx);
      }
    });
    this.ayarlarAcikSet = yeniSet;
    this.parametreler$.next(this.parametreler);
    this.CD.detectChanges();
    this.degisti.emit(this.parametreler);
  }

  // 🚀 Yeni Parametre Metodları

  /** Konum bilgisini al (GPS) */
  konumAl(itm: any): void {
    if ('geolocation' in navigator) {
      this.nzNotification.info("Konum Alınıyor", "GPS konumunuz alınıyor...");

      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (!itm.degeri) itm.degeri = {};
          itm.degeri.enlem = position.coords.latitude;
          itm.degeri.boylam = position.coords.longitude;

          this.nzNotification.success("Başarılı", "Konum başarıyla alındı");
          this.CD.detectChanges();
          this.degisti.emit(this.parametreler);
        },
        (error) => {
          this.nzNotification.error("Hata", "Konum alınamadı: " + error.message);
        }
      );
    } else {
      this.nzNotification.error("Hata", "Tarayıcınız konum özelliğini desteklemiyor");
    }
  }

  /** Rich Text değişimini yönet */
  richTextDegisti(itm: any, event: any): void {
    itm.degeri = event.target.innerHTML;
    this.degisiklikler$.next({ parametre: itm, deger: itm.degeri });
  }

  /** Rich Text komutu uygula */
  richTextKomutUygula(itm: any, komut: string): void {
    document.execCommand(komut, false, null);
    itm.degeri = (event.target as HTMLElement).innerHTML;
    this.degisiklikler$.next({ parametre: itm, deger: itm.degeri });
  }

  /** 🚀 Validasyonları geliştirilmiş kontrol */
  async tumParametreleriKontrolEt(): Promise<{ gecerli: boolean; hatalar: any[]; uyarilar: any[] }> {
    const sonuc = { gecerli: true, hatalar: [], uyarilar: [] };

    if (!this.parametreler || this.parametreler.length === 0) {
      return sonuc;
    }

    // Parametreleri recursive olarak kontrol et
    const parametreleriKontrolEt = async (parametreler: IParametreTumu[]): Promise<void> => {
      for (const parametre of parametreler) {
        if (!this.gorebilirMi(parametre)) continue;

        // Temel validasyon
        await kontrolDeger(parametre, this.parametreler, this.kart);

        if (parametre.hatalar?.length > 0) {
          sonuc.gecerli = false;
          sonuc.hatalar.push(...parametre.hatalar.map(h => ({ ...h, parametre: parametre.baslik })));
        }

        if (parametre.uyarilar?.length > 0) {
          sonuc.uyarilar.push(...parametre.uyarilar.map(u => ({ ...u, parametre: parametre.baslik })));
        }

        // Alt parametreleri kontrol et
        if (parametre.turu === 'Grup' && Array.isArray(parametre.degeri)) {
          await parametreleriKontrolEt(parametre.degeri as IParametreTumu[]);
        } else if (parametre.turu === 'Sekme' && Array.isArray(parametre.degeri)) {
          for (const sekme of parametre.degeri) {
            if (Array.isArray(sekme.degeri)) {
              await parametreleriKontrolEt(sekme.degeri as IParametreTumu[]);
            }
          }
        }
      }
    };

    await parametreleriKontrolEt(this.parametreler);

    if (!sonuc.gecerli) {
      const gorunenler = this.gosterilecekParametreler;
      gorunenler.forEach((p, i) => {
        if (p.hatalar?.length > 0) this.hataAnimasyonuBaslat(i);
      });
    }

    return sonuc;
  }

  /** 🚀 Form verilerini dışa aktar */
  verileriDisaAktar(format: 'json' | 'csv' | 'xml' = 'json'): void {
    const veri = this.toJson();

    switch (format) {
      case 'json':
        this.jsonIndir(veri, 'parametreler.json');
        break;
      case 'csv':
        this.csvIndir(veri, 'parametreler.csv');
        break;
      case 'xml':
        this.xmlIndir(veri, 'parametreler.xml');
        break;
    }
  }

  /** JSON dosyası indir */
  private jsonIndir(veri: any, dosyaAdi: string): void {
    const json = JSON.stringify(veri, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    this.dosyaIndir(blob, dosyaAdi);
  }

  /** CSV dosyası indir */
  private csvIndir(veri: any, dosyaAdi: string): void {
    let csv = 'Parametre,Değer,Tür\n';

    const csvOlustur = (obj: any, prefix = ''): void => {
      Object.keys(obj).forEach(key => {
        const value = obj[key];
        const fullKey = prefix ? `${prefix}.${key}` : key;

        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          csvOlustur(value, fullKey);
        } else {
          csv += `"${fullKey}","${value}","${typeof value}"\n`;
        }
      });
    };

    csvOlustur(veri);
    const blob = new Blob([csv], { type: 'text/csv' });
    this.dosyaIndir(blob, dosyaAdi);
  }

  /** XML dosyası indir */
  private xmlIndir(veri: any, dosyaAdi: string): void {
    let xml = '<?xml version="1.0" encoding="UTF-8"?>\n<parametreler>\n';

    const xmlOlustur = (obj: any, indent = 1): void => {
      const space = '  '.repeat(indent);

      Object.keys(obj).forEach(key => {
        const value = obj[key];

        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          xml += `${space}<${key}>\n`;
          xmlOlustur(value, indent + 1);
          xml += `${space}</${key}>\n`;
        } else {
          xml += `${space}<${key}>${this.xmlEscape(value.toString())}</${key}>\n`;
        }
      });
    };

    xmlOlustur(veri);
    xml += '</parametreler>';

    const blob = new Blob([xml], { type: 'application/xml' });
    this.dosyaIndir(blob, dosyaAdi);
  }

  /** XML için escape */
  private xmlEscape(str: string): string {
    return str.replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /** Dosya indirme */
  private dosyaIndir(blob: Blob, dosyaAdi: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = dosyaAdi;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /** 🚀 Parametreleri kopyala */
  parametreleriKopyala(kaynak: IParametreTumu[], hedef: IParametreTumu[]): void {
    // Derin kopyalama işlemi
    const kopyala = (obj: any): any => {
      if (obj === null || typeof obj !== 'object') return obj;
      if (obj instanceof Date) return new Date(obj.getTime());
      if (obj instanceof Array) return obj.map(item => kopyala(item));

      const kopya: any = {};
      Object.keys(obj).forEach(key => {
        kopya[key] = kopyala(obj[key]);
      });
      return kopya;
    };

    hedef.length = 0;
    hedef.push(...kopyala(kaynak));

    this.nzNotification.success("Başarılı", "Parametreler kopyalandı");
    this.CD.detectChanges();
  }

  /** 🚀 Form sıfırlama (gelişmiş) */
  formuSifirla(onayIstegi: boolean = true): Promise<boolean> {
    return new Promise((resolve) => {
      if (onayIstegi) {
        this.nzModalServis.confirm({
          nzTitle: 'Emin misiniz?',
          nzContent: 'Tüm form verileri sıfırlanacaktır. Bu işlem geri alınamaz.',
          nzOkText: 'Evet, Sıfırla',
          nzCancelText: 'İptal',
          nzOnOk: () => {
            this.sifirla(this.parametreler);
            this.nzNotification.success("Başarılı", "Form sıfırlandı");
            this.CD.detectChanges();
            resolve(true);
          },
          nzOnCancel: () => resolve(false)
        });
      } else {
        this.sifirla(this.parametreler);
        resolve(true);
      }
    });
  }

  /** 🚀 Zaman damgası ekle */
  zamanDamgasiEkle(): void {
    const yeniParametre: IParametreTumu = {
      turu: "Tarih Saat",
      ismi: `zamanDamgasi_${Date.now()}`,
      baslik: "Zaman Damgası",
      altAciklama: "Otomatik oluşturulmuş zaman damgası",
      degeri: new Date(),
      ayarlar: {
        sadeceOkunur: true,
        format: "dd.MM.yyyy HH:mm:ss"
      }
    };

    if (!this.parametreler) {
      this.parametreler = [];
    }

    this.parametreler.push(yeniParametre);
    this.nzNotification.success("Başarılı", "Zaman damgası eklendi");

    // 🚀 BehaviorSubject'i güncelle
    this.parametreler$.next(this.parametreler);

    this.CD.detectChanges();
    this.degisti.emit(this.parametreler);
  }

  // NOT: Modal editör tamamen kaldırıldı, tüm düzenleme inline gelişmiş ayarlar panelinden yapılıyor

  // 🎯 UI Özellikleri
  seciliFiltreler: any = {};
  siralamaKriteri: any = { alan: 'baslik', yon: 'asc' };
  aramaAktif: boolean = false;

  // NOT: Tüm düzenleme inline gelişmiş ayarlar panelinden yapılıyor

  // 🎯 Yardımcı Metodlar
  getParametreIkonu(turu: string): string {
    const ikonlar: { [key: string]: string } = {
      'Yazı': '✏️',
      'Yazı Alanı': '📝',
      'Parola': '🔒',
      'Sayı': '🔢',
      'Tarih': '📅',
      'Tarih Saat': '📅🕓',
      'Onay': '☑️',
      'Anahtar': '🔘',
      'Renk': '🎨',
      'Açılır Kutu': '📋',
      'İlerleme Çubuğu': '📊',
      'Oylama': '⭐',
      'Konum': '📍'
    };
    return ikonlar[turu] || '❓';
  }

  getParametreIconRengi(turu: string): string {
    const renkler: { [key: string]: string } = {
      'Yazı': '#1890ff',
      'Yazı Alanı': '#52c41a',
      'Parola': '#faad14',
      'Sayı': '#722ed1',
      'Tarih': '#13c2c2',
      'Tarih Saat': '#13c2c2',
      'Onay': '#52c41a',
      'Anahtar': '#fa8c16',
      'Renk': '#eb2f96',
      'Açılır Kutu': '#1890ff',
      'İlerleme Çubuğu': '#1890ff',
      'Oylama': '#faad14',
      'Konum': '#52c41a'
    };
    return renkler[turu] || '#d9d9d9';
  }

  getTurRengi(turu: string): string {
    const renkler: { [key: string]: string } = {
      'Yazı': 'blue',
      'Yazı Alanı': 'green',
      'Parola': 'orange',
      'Sayı': 'purple',
      'Tarih': 'cyan',
      'Tarih Saat': 'cyan',
      'Onay': 'green',
      'Anahtar': 'orange',
      'Renk': 'magenta',
      'Açılır Kutu': 'blue',
      'İlerleme Çubuğu': 'blue',
      'Oylama': 'orange',
      'Konum': 'green'
    };
    return renkler[turu] || 'default';
  }

  getKategoriRengi(kategori: string): string {
    const renkler: { [key: string]: string } = {
      'Yazı': 'blue',
      'Sayı': 'purple',
      'Tarih': 'cyan',
      'Seçim': 'green',
      'Dosya': 'orange',
      'Görsel': 'magenta'
    };
    return renkler[kategori] || 'default';
  }

  getInputStatus(itm: any): 'error' | 'warning' | '' {
    if (itm?.hatalar?.length > 0) return 'error';
    if (itm?.uyarilar?.length > 0) return 'warning';
    return '';
  }

  getInputSuffix(itm: any): string {
    if (itm?.ayarlar?.sagSimge) return itm.ayarlar.sagSimge;
    return '';
  }

  // 🎯 Ek yardımcı metodlar
  showPassword: boolean = false;
  passwordSuffix = {
    icon: this.showPassword ? 'eye-invisible' : 'eye',
    handler: () => {
      this.showPassword = !this.showPassword;
      this.CD.detectChanges();
    }
  };

  getPasswordStrength(password: string): number {
    if (!password) return 0;
    let strength = 0;
    if (password.length >= 8) strength += 0.25;
    if (/[a-z]/.test(password)) strength += 0.25;
    if (/[A-Z]/.test(password)) strength += 0.25;
    if (/[0-9]/.test(password)) strength += 0.25;
    return strength;
  }

  getPasswordStrengthColor(password: string): string {
    const strength = this.getPasswordStrength(password);
    if (strength <= 0.25) return '#ff4d4f';
    if (strength <= 0.5) return '#faad14';
    if (strength <= 0.75) return '#1890ff';
    return '#52c41a';
  }

  getPasswordStrengthText(password: string): string {
    const strength = this.getPasswordStrength(password);
    if (strength <= 0.25) return 'Zayıf';
    if (strength <= 0.5) return 'Orta';
    if (strength <= 0.75) return 'Güçlü';
    return 'Çok Güçlü';
  }

  getNumberPercentage(value: number, min?: number, max?: number): number {
    if (min === undefined) min = 0;
    if (max === undefined) max = 100;
    return ((value - min) / (max - min)) * 100;
  }

  formatNumber(value: number): string {
    return new Intl.NumberFormat('tr-TR').format(value);
  }

  formatDate(date: Date, format?: string): string {
    if (!date) return '';
    if (format) {
      // Format string'ini işle (basit implementasyon)
      return date.toLocaleDateString('tr-TR');
    }
    return date.toLocaleDateString('tr-TR');
  }

  formatDateTime(date: Date, format?: string): string {
    if (!date) return '';
    if (format) {
      // Format string'ini işle (basit implementasyon)
      return date.toLocaleString('tr-TR');
    }
    return date.toLocaleString('tr-TR');
  }

  getGunAdi(date: Date): string {
    if (!date) return '';
    const gunler = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
    return gunler[date.getDay()];
  }

  getTimeAgo(date: Date): string {
    if (!date) return '';
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'az önce';
    if (minutes < 60) return `${minutes} dakika önce`;
    if (hours < 24) return `${hours} saat önce`;
    return `${days} gün önce`;
  }

  getRenkAdi(hexColor: string): string {
    // Basit renk isimleri
    const renkler: { [key: string]: string } = {
      '#FF0000': 'Kırmızı',
      '#00FF00': 'Yeşil',
      '#0000FF': 'Mavi',
      '#FFFF00': 'Sarı',
      '#FF00FF': 'Magenta',
      '#00FFFF': 'Cyan',
      '#000000': 'Siyah',
      '#FFFFFF': 'Beyaz',
      '#808080': 'Gri'
    };
    return renkler[hexColor.toUpperCase()] || hexColor;
  }

  renkKopyala(renk: string): void {
    navigator.clipboard.writeText(renk).then(() => {
      this.nzNotification.success('Başarılı', 'Renk kodu kopyalandı');
    }).catch(() => {
      this.nzNotification.error('Hata', 'Renk kodu kopyalanamadı');
    });
  }

  filterOption(input: string, option: any): boolean {
    return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
  }

  getSeciliLabel(itm: any): string {
    if (!itm.degeri || !itm.ayarlar?.secenekler) return '';
    const secenek = itm.ayarlar.secenekler.find((opt: any) => opt.value === itm.degeri);
    return secenek ? secenek.label : itm.degeri;
  }

  getSecenekLabel(itm: any, value: any): string {
    if (!itm.ayarlar?.secenekler) return value;
    const secenek = itm.ayarlar.secenekler.find((opt: any) => opt.value === value);
    return secenek ? secenek.label : value;
  }

  removeFromMultiple(itm: any, value: any): void {
    if (Array.isArray(itm.degeri)) {
      const index = itm.degeri.indexOf(value);
      if (index > -1) {
        itm.degeri.splice(index, 1);
        this.kontrolDeger(itm, this.parametreler, this.kart);
        this.CD.detectChanges();
      }
    }
  }

  getProgressColor(value: number, max?: number): string {
    const percentage = max ? (value / max) * 100 : value;
    if (percentage >= 100) return '#52c41a';
    if (percentage >= 75) return '#1890ff';
    if (percentage >= 50) return '#faad14';
    return '#ff4d4f';
  }

  getProgressStatus(value: number, max?: number): 'normal' | 'exception' | 'active' | 'success' {
    const percentage = max ? (value / max) * 100 : value;
    if (percentage >= 100) return 'success';
    if (percentage >= 75) return 'active';
    if (percentage < 25) return 'exception';
    return 'normal';
  }

  getProgressGradient(value: number, max?: number): string {
    const percentage = max ? (value / max) * 100 : value;
    if (percentage >= 75) return '#52c41a';
    if (percentage >= 50) return '#1890ff';
    if (percentage >= 25) return '#faad14';
    return '#ff4d4f';
  }

  setRating(itm: any, rating: number): void {
    itm.degeri = rating;
    this.kontrolDeger(itm, this.parametreler, this.kart);
    this.CD.detectChanges();
  }

  getStarColor(starIndex: number, currentRating: number): string {
    return starIndex <= currentRating ? '#fadb14' : '#d9d9d9';
  }

  getStarIcon(starIndex: number, currentRating: number): string {
    return starIndex <= currentRating ? '★' : '☆';
  }

  getStarTooltip(starIndex: number): string {
    const yildizMetni = ['Çok Kötü', 'Kötü', 'Orta', 'İyi', 'Çok İyi'];
    return `${starIndex} Yıldız - ${yildizMetni[starIndex - 1]}`;
  }

  getRatingText(rating: number): string {
    const metinler = ['', 'Çok Kötü', 'Kötü', 'Orta', 'İyi', 'Mükemmel'];
    return metinler[rating] || '';
  }

  konumAc(itm: any): void {
    if (itm.degeri?.lat && itm.degeri?.lng) {
      const url = `https://www.google.com/maps?q=${itm.degeri.lat},${itm.degeri.lng}`;
      window.open(url, '_blank');
    }
  }

  konumKopyala(itm: any): void {
    if (itm.degeri?.lat && itm.degeri?.lng) {
      const kordinatlar = `${itm.degeri.lat}, ${itm.degeri.lng}`;
      navigator.clipboard.writeText(kordinatlar).then(() => {
        this.nzNotification.success('Başarılı', 'Kordinatlar kopyalandı');
      }).catch(() => {
        this.nzNotification.error('Hata', 'Kordinatlar kopyalanamadı');
      });
    }
  }

  parametreSıfırla(itm: any): void {
    itm.degeri = null;
    itm.hatalar = [];
    itm.uyarilar = [];
    this.kontrolDeger(itm, this.parametreler, this.kart);
    this.CD.detectChanges();
  }

  addValidationRule(type: string): void {
    // Validasyon kuralı ekleme implementasyonu
    this.nzNotification?.info('Bilgi', `${type} validasyon kuralı eklendi`);
  }

  // 🎯 Yetki şablonlarını getir
  private getYetkiSablonlari(): any {
    return {
      roller: [
        'admin', 'editor', 'parametre_yoneticisi', 'kullanici', 'misafir'
      ],
      yetkiTipleri: [
        { value: 'gorebilir', label: 'Görebilir' },
        { value: 'degistirebilir', label: 'Değiştirebilir' },
        { value: 'silebilir', label: 'Silebilir' }
      ],
      varsayilanYetkiler: {
        'tum': { gorebilir: [], degistirebilir: [], silebilir: [] },
        'rollerineGore': {
          gorebilir: ['admin', 'editor', 'kullanici'],
          degistirebilir: ['admin', 'editor'],
          silebilir: ['admin']
        },
        'kisiyeOzel': {
          gorebilir: [],
          degistirebilir: [],
          silebilir: []
        }
      }
    };
  }

  // 🎯 Parametre türüne özel ayarlar getir
  getParametreTuruAyarlari(turu: string): any {
    const ayarlar: { [key: string]: any } = {
      'Yazı': {
        validasyonlar: ['required', 'minLength', 'maxLength', 'regex'],
        ozellikler: ['placeholder', 'maxLength', 'prefix', 'suffix'],
        varsayilanValidasyon: {
          minLength: 1,
          maxLength: 255
        }
      },
      'Yazı Alanı': {
        validasyonlar: ['required', 'minLength', 'maxLength'],
        ozellikler: ['rows', 'placeholder', 'maxLength', 'autoResize'],
        varsayilanValidasyon: {
          minLength: 1,
          maxLength: 1000,
          rows: 3
        }
      },
      'Parola': {
        validasyonlar: ['required', 'minLength', 'maxLength', 'passwordStrength'],
        ozellikler: ['placeholder', 'showPassword', 'strengthIndicator'],
        varsayilanValidasyon: {
          minLength: 8,
          maxLength: 128
        }
      },
      'Sayı': {
        validasyonlar: ['required', 'min', 'max', 'step', 'integer', 'positive'],
        ozellikler: ['min', 'max', 'step', 'precision', 'prefix', 'suffix'],
        varsayilanValidasyon: {
          min: 0,
          max: 999999999
        }
      },
      'Tarih': {
        validasyonlar: ['required', 'minDate', 'maxDate'],
        ozellikler: ['format', 'minDate', 'maxDate', 'disabledDates'],
        varsayilanValidasyon: {
          format: 'dd.MM.yyyy'
        }
      },
      'Tarih Saat': {
        validasyonlar: ['required', 'minDate', 'maxDate'],
        ozellikler: ['format', 'minDate', 'maxDate', 'showTime', 'disabledDates'],
        varsayilanValidasyon: {
          format: 'dd.MM.yyyy HH:mm'
        }
      },
      'Onay': {
        validasyonlar: ['required'],
        ozellikler: ['label', 'checkedValue', 'uncheckedValue'],
        varsayilanValidasyon: {}
      },
      'Anahtar': {
        validasyonlar: ['required'],
        ozellikler: ['label', 'checkedValue', 'uncheckedValue'],
        varsayilanValidasyon: {}
      },
      'Renk': {
        validasyonlar: ['required'],
        ozellikler: ['format', 'presets', 'showAlpha'],
        varsayilanValidasyon: {
          format: 'hex'
        }
      },
      'Açılır Kutu': {
        validasyonlar: ['required'],
        ozellikler: ['options', 'multiple', 'searchable', 'clearable', 'api'],
        varsayilanValidasyon: {}
      },
      'Resim': {
        validasyonlar: ['required', 'fileType', 'fileSize'],
        ozellikler: ['accept', 'maxSize', 'preview', 'cropper'],
        varsayilanValidasyon: {
          accept: 'image/*',
          maxSize: 10 // MB
        }
      },
      'Video': {
        validasyonlar: ['required', 'fileType', 'fileSize'],
        ozellikler: ['accept', 'maxSize', 'preview', 'duration'],
        varsayilanValidasyon: {
          accept: 'video/*',
          maxSize: 50 // MB
        }
      },
      'Ses': {
        validasyonlar: ['required', 'fileType', 'fileSize'],
        ozellikler: ['accept', 'maxSize', 'preview', 'duration'],
        varsayilanValidasyon: {
          accept: 'audio/*',
          maxSize: 20 // MB
        }
      },
      'Dosya': {
        validasyonlar: ['required', 'fileType', 'fileSize'],
        ozellikler: ['accept', 'maxSize', 'multiple'],
        varsayilanValidasyon: {
          maxSize: 25 // MB
        }
      },
      'XYZ': {
        validasyonlar: ['required'],
        ozellikler: ['xMin', 'xMax', 'yMin', 'yMax', 'zMin', 'zMax', 'step'],
        varsayilanValidasyon: {}
      }
    };

    return ayarlar[turu] || {
      validasyonlar: ['required'],
      ozellikler: [],
      varsayilanValidasyon: {}
    };
  }

  // 🎯 Parametre kopyala (yetki kontrolü ile)
  parametreyiKopyala(parametre: IParametreTumu, index: number): void {
    if (!this.duzenleyebilirMi(parametre)) {
      this.nzNotification?.error('Yetki Hatası', 'Bu parametreyi kopyalama yetkiniz bulunmuyor');
      return;
    }

    const kopya = JSON.parse(JSON.stringify(parametre));
    kopya.ismi = `${parametre.ismi}_kopya_${Date.now()}`;
    kopya.baslik = `${parametre.baslik} (Kopya)`;

    this.parametreler.splice(index + 1, 0, kopya);
    this.nzNotification?.success('Başarılı', `"${parametre.baslik}" parametresi kopyalandı`);

    this.parametreler$.next(this.parametreler);
    this.CD.detectChanges();
    this.degisti.emit(this.parametreler);
  }

  // 🎯 Parametre taşı (drag & drop için)
  parametreyiTasi(fromIndex: number, toIndex: number): void {
    if (!this.parametreEkleyebilirMi()) {
      this.nzNotification?.error('Yetki Hatası', 'Parametreleri düzenleme yetkiniz bulunmuyor');
      return;
    }

    const [taşınan] = this.parametreler.splice(fromIndex, 1);
    this.parametreler.splice(toIndex, 0, taşınan);

    this.parametreler$.next(this.parametreler);
    this.CD.detectChanges();
    this.degisti.emit(this.parametreler);
  }

  // 🎯 Gelişmiş arama
  gelismisArama(kriter: any): void {
    let sonuc = this.parametreler || [];

    if (kriter.terim) {
      const terim = kriter.terim.toLowerCase();
      sonuc = sonuc.filter(p =>
        this.gorebilirMi(p) && (
          p.baslik?.toLowerCase().includes(terim) ||
          p.ismi?.toLowerCase().includes(terim) ||
          p.altAciklama?.toLowerCase().includes(terim) ||
          p.ipucu?.toLowerCase().includes(terim)
        )
      );
    }

    if (kriter.turu && kriter.turu !== 'tum') {
      sonuc = sonuc.filter(p => p.turu === kriter.turu);
    }

    if (kriter.zorunlu !== undefined) {
      sonuc = sonuc.filter(p => p.ayarlar?.zorunlu === kriter.zorunlu);
    }

    if (kriter.sadeceOkunur !== undefined) {
      sonuc = sonuc.filter(p => p.ayarlar?.sadeceOkunur === kriter.sadeceOkunur);
    }

    if (kriter.hataliVar) {
      sonuc = sonuc.filter(p => p.hatalar?.length > 0);
    }

    this.aramaSonuclari = sonuc;
    this.CD.detectChanges();
  }

  // 🎯 Filtreleri temizle
  filtreleriTemizle(): void {
    this.aramaTerimi = '';
    this.secilenKategori = 'tum';
    this.aramaSonuclari = this.parametreler || [];
    this.filtrelenmisParametreler$.next(this.parametreler || []);
    this.cache.clear();
    this.CD.detectChanges();
  }

  // 🎯 Filtreleme metodları
  filtreyiToggle(filtreTipi: string): void {
    this.seciliFiltreler[filtreTipi] = !this.seciliFiltreler[filtreTipi];
    this.filtreleriUygula();
  }

  filtreyiKaldir(filtreTipi: string): void {
    delete this.seciliFiltreler[filtreTipi];
    this.filtreleriUygula();
  }

  filtreleriUygula(): void {
    const kriter: any = { terim: this.aramaTerimi };

    if (this.seciliFiltreler.zorunlu) {
      kriter.zorunlu = true;
    }

    if (this.seciliFiltreler.hatali) {
      kriter.hataliVar = true;
    }

    if (this.secilenKategori && this.secilenKategori !== 'tum') {
      kriter.turu = this.secilenKategori;
    }

    this.gelismisArama(kriter);
  }

  // 🎯 Sıralama metodları
  parametreleriSirala(alan: string): void {
    const yon = this.siralamaKriteri.alan === alan && this.siralamaKriteri.yon === 'asc' ? 'desc' : 'asc';
    this.siralamaKriteri = { alan, yon };

    const sirali = [...(this.aramaTerimi ? this.aramaSonuclari : this.filtrelenmisParametreler$.value || [])];

    sirali.sort((a, b) => {
      let degerA = a[alan as keyof IParametreTumu];
      let degerB = b[alan as keyof IParametreTumu];

      if (typeof degerA === 'string' && typeof degerB === 'string') {
        degerA = degerA.toLowerCase();
        degerB = degerB.toLowerCase();
      }

      if (yon === 'asc') {
        return degerA > degerB ? 1 : degerA < degerB ? -1 : 0;
      } else {
        return degerA < degerB ? 1 : degerA > degerB ? -1 : 0;
      }
    });

    if (this.aramaTerimi) {
      this.aramaSonuclari = sirali;
    } else {
      this.filtrelenmisParametreler$.next(sirali);
    }

    this.CD.detectChanges();
  }

  // 🎯 Arama kontrol metodları
  aramaAc(): void {
    this.aramaAktif = true;
    this.CD.detectChanges();
  }

  aramaKapat(): void {
    this.aramaAktif = false;
    this.aramaTerimi = '';
    this.filtreleriTemizle();
    this.CD.detectChanges();
  }

  // 🎯 Görünüm modu değiştirme
  gorunumModuDegisti(mod: string): void {
    this.gorunumModu = mod as any;
    this.CD.detectChanges();
  }

  // 🎯 Parametre değeri değiştiğinde
  onParametreDegerDegisti(event: { parametre: IParametreTumu, deger: any }): void {
    const { parametre, deger } = event;
    if (parametre) {
      parametre.degeri = deger;
      kontrolDeger(parametre, this.parametreler, this.kart);
      this.CD.detectChanges();
      this.degisti.emit(this.parametreler);
    }
  }

  // 🎯 Dosya seçimi için metodlar
  tiklandiResimSecDegistir(parametre: IParametreTumu): void {
    if (!this.duzenleyebilirMi(parametre)) return;

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (event: any) => {
      const file = event.target.files[0];
      if (file) {
        this.dosyaIsle(parametre, file);
      }
    };
    input.click();
  }

  tiklandiVideoSecDegistir(parametre: IParametreTumu): void {
    if (!this.duzenleyebilirMi(parametre)) return;

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'video/*';
    input.onchange = (event: any) => {
      const file = event.target.files[0];
      if (file) {
        this.dosyaIsle(parametre, file);
      }
    };
    input.click();
  }

  tiklandiSesSecDegistir(parametre: IParametreTumu): void {
    if (!this.duzenleyebilirMi(parametre)) return;

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'audio/*';
    input.onchange = (event: any) => {
      const file = event.target.files[0];
      if (file) {
        this.dosyaIsle(parametre, file);
      }
    };
    input.click();
  }

  tiklandiDosyaSecDegistir(parametre: IParametreTumu): void {
    if (!this.duzenleyebilirMi(parametre)) return;

    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = (event: any) => {
      const file = event.target.files[0];
      if (file) {
        this.dosyaIsle(parametre, file);
      }
    };
    input.click();
  }

  private dosyaIsle(parametre: IParametreTumu, file: File): void {
    const _id = new FikoYTId();
    (parametre.degeri as any) = {
      _id,
      dosya: file,
      objectUrl: this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(file))
    };

    kontrolDeger(parametre, this.parametreler, this.kart);
    this.CD.detectChanges();
    this.degisti.emit(this.parametreler);
  }

  // 🎯 Medya silme metodu (Resim, Video, Ses için ortak)
  tiklandiMedyaSil(parametre: IParametreTumu): void {
    if (!this.duzenleyebilirMi(parametre)) return;

    if ((parametre.degeri as any)?.objectUrl) {
      URL.revokeObjectURL((parametre.degeri as any).objectUrl);
    }
    parametre.degeri = null;
    kontrolDeger(parametre, this.parametreler, this.kart);
    this.CD.detectChanges();
    this.degisti.emit(this.parametreler);
  }

  // Geriye uyumluluk için alias metodlar
  tiklandiResimSil = this.tiklandiMedyaSil;
  tiklandiVideoSil = (p: IParametreTumu) => this.tiklandiMedyaSil(p);
  tiklandiSesSil = (p: IParametreTumu) => this.tiklandiMedyaSil(p);

  // Not: tiklandiVideoSil ve tiklandiSesSil metodları tiklandiMedyaSil olarak birleştirildi

  // NOT: Modal editör kaldırıldı, turuneGoreVarsayilanDeger bu sınıfta tanımlı

  // --- Liste Parametre Metodları ---

  listeElemanEkle(itm: any, deger: string) {
    if (!deger?.trim()) return;
    if (!itm.degeri) itm.degeri = [];
    itm.degeri.push(deger.trim());
    this.inlineAyarDegisti();
  }

  listeElemanSil(itm: any, index: number) {
    itm.degeri?.splice(index, 1);
    this.inlineAyarDegisti();
  }

  // --- Tablo Parametre Metodları ---

  tabloSatirEkle(itm: any) {
    if (!itm.degeri) itm.degeri = { kolonlar: [], satirlar: [] };
    if (!itm.degeri.kolonlar?.length) {
      itm.degeri.kolonlar = itm?.ayarlar?.kolonlar ?? ['Kolon 1'];
    }
    if (!itm.degeri.satirlar) itm.degeri.satirlar = [];
    const yeniSatir: any = {};
    for (const k of itm.degeri.kolonlar) {
      yeniSatir[k] = '';
    }
    itm.degeri.satirlar.push(yeniSatir);
    this.inlineAyarDegisti();
  }

  tabloSatirSil(itm: any, index: number) {
    itm.degeri?.satirlar?.splice(index, 1);
    this.inlineAyarDegisti();
  }

  tabloKolonlarGuncelle(itm: any, str: string) {
    if (!itm.ayarlar) itm.ayarlar = {};
    itm.ayarlar.kolonlar = (str || '').split(',').map((k: string) => k.trim()).filter((k: string) => k);
    this.inlineAyarDegisti();
  }

  tabloKolonEkle(itm: any) {
    if (!itm.degeri) itm.degeri = { kolonlar: [], satirlar: [] };
    if (!itm.degeri.kolonlar) itm.degeri.kolonlar = [];
    const yeniKolonAd = 'Kolon ' + (itm.degeri.kolonlar.length + 1);
    itm.degeri.kolonlar.push(yeniKolonAd);
    for (const satir of (itm.degeri.satirlar ?? [])) {
      satir[yeniKolonAd] = '';
    }
    this.inlineAyarDegisti();
  }

  // --- Aranjman Parametre Metodları ---

  aranjmanElemanEkle(itm: any, deger: string) {
    if (!deger?.trim()) return;
    if (!itm.degeri) itm.degeri = [];
    itm.degeri.push(deger.trim());
    this.inlineAyarDegisti();
  }

  aranjmanElemanSil(itm: any, index: number) {
    itm.degeri?.splice(index, 1);
    this.inlineAyarDegisti();
  }

  aranjmanSiraDegisti(itm: any, event: any) {
    if (!itm.degeri) return;
    const onceki = event.previousIndex;
    const sonraki = event.currentIndex;
    const eleman = itm.degeri.splice(onceki, 1)[0];
    itm.degeri.splice(sonraki, 0, eleman);
    this.inlineAyarDegisti();
  }

  // --- İmza Parametre Metodları ---

  imzaBaslat(itm: any) {
    this.nzNotification.info('İmza', 'İmza alanı hazırlanıyor... Canvas tabanlı imza için tam ekran gerekir.');
    // Basit bir çözüm: prompt ile base64 imza alma
    // Gerçek projede canvas-based signature pad kullanılacak
  }

  imzaTemizle(itm: any) {
    itm.degeri = null;
    this.inlineAyarDegisti();
  }

  // --- Çizim Parametre Metodları ---

  cizimBaslat(itm: any) {
    this.nzNotification.info('Çizim', 'Çizim alanı hazırlanıyor...');
  }

  cizimTemizle(itm: any) {
    itm.degeri = null;
    this.inlineAyarDegisti();
  }

  // --- Matematik Parametre Metodları ---

  matematikHesapla(ifade: string): string {
    try {
      const temiz = ifade
        .replace(/π/g, 'Math.PI')
        .replace(/√/g, 'Math.sqrt')
        .replace(/\^/g, '**');
      const sonuc = Function('"use strict"; return (' + temiz + ')')();
      return typeof sonuc === 'number' ? sonuc.toFixed(6).replace(/\.?0+$/, '') : String(sonuc);
    } catch (e) {
      console.warn('[Parametre] Matematik hesaplama hatası:', e);
      return 'Hatalı ifade';
    }
  }

  // --- RichText Parametre Metodları ---

  richTextKomut(komut: string) {
    document.execCommand(komut, false, null);
  }

  // --- Panoya Kopyala (genel) ---

  async otomatikKodUret(itm: any) {
    const format = itm.ayarlar?.kodFormat || '{PREFIX}-{YIL}-{SAYI:3}';
    const now = new Date();
    let sonrakiSayi = 1;

    try {
      const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.DB.hareketler.aggregate' });
      istek.parametreler = {
        aggregate: [
          { $match: { vt: 'FikoYT.sistem.hareketler', silindi: { $ne: true } } },
          { $unwind: '$veri.icerikler' },
          { $unwind: '$veri.icerikler.ek.parametreler' },
          { $match: { 'veri.icerikler.ek.parametreler.ismi': itm.ismi, 'veri.icerikler.ek.parametreler.turu': 'Otomatik Kod' } },
          { $sort: { _id: -1 } },
          { $limit: 1 },
          { $project: { deger: '$veri.icerikler.ek.parametreler.degeri' } },
        ]
      };
      const cevap = await istek.isteSync();
      const sonKod = cevap?.cevap?.sonuc?.[0]?.deger;
      if (sonKod) {
        const sayiMatch = sonKod.match(/(\d+)\s*$/);
        if (sayiMatch) sonrakiSayi = parseInt(sayiMatch[1], 10) + 1;
      }
    } catch { }

    let kod = format
      .replace(/\{PREFIX\}/gi, itm.ayarlar?.kodPrefix ?? 'PRJ')
      .replace(/\{YIL\}/gi, '' + now.getFullYear())
      .replace(/\{AY\}/gi, String(now.getMonth() + 1).padStart(2, '0'))
      .replace(/\{GUN\}/gi, String(now.getDate()).padStart(2, '0'))
      .replace(/\{SAAT\}/gi, String(now.getHours()).padStart(2, '0'))
      .replace(/\{DAKIKA\}/gi, String(now.getMinutes()).padStart(2, '0'));

    kod = kod.replace(/\{SAYI(?::(\d+))?\}/gi, (_, hane) =>
      String(sonrakiSayi).padStart(parseInt(hane || '3', 10), '0')
    );

    // Parametre referansları — {parametreIsmi} ile aynı setteki diğer parametrelerin değerlerini al
    const sistemDegiskenler = ['PREFIX', 'YIL', 'AY', 'GUN', 'SAAT', 'DAKIKA'];
    kod = kod.replace(/\{([^}:]+)\}/g, (eslesme, ismi) => {
      if (sistemDegiskenler.includes(ismi.toUpperCase()) || ismi.toUpperCase().startsWith('SAYI')) return eslesme;
      const param = (this.parametreler ?? []).find((p: any) => p.ismi === ismi);
      return (param?.degeri != null && param.degeri !== '') ? '' + param.degeri : '';
    });

    itm.degeri = kod;
    this.degisti.emit(this.parametreler);
    this.CD.detectChanges();
  }

  panoyaKopyala(deger: string) {
    navigator.clipboard.writeText(deger).then(() => {
      this.nzNotification.success('Kopyalandı', 'Değer panoya kopyalandı');
    });
  }

  // --- Dosya Sürükle Bırak Parametre Metodları ---

  dosyaSurukBirakBirakildi(itm: any, event: DragEvent) {
    event.preventDefault();
    const dosyalar = event.dataTransfer?.files;
    if (dosyalar?.length > 0) {
      const dosya = dosyalar[0];
      itm.degeri = {
        dosya: dosya,
        objectUrl: URL.createObjectURL(dosya),
      };
      this.inlineAyarDegisti();
    }
  }

  dosyaSurukBirakSec(itm: any) {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = itm?.ayarlar?.accept ?? '*/*';
    input.onchange = (e: any) => {
      const dosya = e.target.files?.[0];
      if (dosya) {
        itm.degeri = {
          dosya: dosya,
          objectUrl: URL.createObjectURL(dosya),
        };
        this.inlineAyarDegisti();
        this.CD.detectChanges();
      }
    };
    input.click();
  }

  // --- Dosya Yükleme Parametre Metodları ---

  dosyaYuklemeSecDegistir(itm: any) {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = itm?.ayarlar?.accept ?? '*/*';
    input.onchange = (e: any) => {
      const dosyalar = Array.from(e.target.files ?? []) as File[];
      if (dosyalar.length > 0) {
        if (!itm.degeri) itm.degeri = { dosyalar: [] };
        if (!itm.degeri.dosyalar) itm.degeri.dosyalar = [];
        itm.degeri.dosyalar.push(...dosyalar);
        this.inlineAyarDegisti();
        this.CD.detectChanges();
      }
    };
    input.click();
  }

  dosyaYuklemeSurukBirak(itm: any, event: DragEvent) {
    event.preventDefault();
    const dosyalar = Array.from(event.dataTransfer?.files ?? []) as File[];
    if (dosyalar.length > 0) {
      if (!itm.degeri) itm.degeri = { dosyalar: [] };
      if (!itm.degeri.dosyalar) itm.degeri.dosyalar = [];
      itm.degeri.dosyalar.push(...dosyalar);
      this.inlineAyarDegisti();
      this.CD.detectChanges();
    }
  }

  dosyaYuklemeSil(itm: any, index: number) {
    itm.degeri?.dosyalar?.splice(index, 1);
    this.inlineAyarDegisti();
  }

  private sablonlariYukle() {
    try {
      const kayitli = localStorage.getItem('fikoyt-parametre-sablonlari');
      if (kayitli) this.sablonListesi = JSON.parse(kayitli);
    } catch { }
  }

  sablonKaydet() {
    if (!this.yeniSablonIsim?.trim() || !this.parametreler?.length) return;
    this.sablonListesi.push({
      isim: this.yeniSablonIsim.trim(),
      parametreler: JSON.parse(JSON.stringify(this.parametreler)),
      tarih: new Date().toISOString()
    });
    localStorage.setItem('fikoyt-parametre-sablonlari', JSON.stringify(this.sablonListesi));
    this.yeniSablonIsim = '';
    this.nzMessage?.success('Şablon kaydedildi');
  }

  sablonYukle(sablon: { isim: string; parametreler: IParametreTumu[]; tarih: string }) {
    this.parametreler.length = 0;
    this.parametreler.push(...JSON.parse(JSON.stringify(sablon.parametreler)));
    this.inlineAyarDegisti();
    this.nzMessage?.success('Şablon yüklendi');
    this.sablonModalAcik = false;
  }

  sablonSil(index: number) {
    this.sablonListesi.splice(index, 1);
    localStorage.setItem('fikoyt-parametre-sablonlari', JSON.stringify(this.sablonListesi));
  }

  // --- Undo/Redo ---

  undoKaydet() {
    if (this.undoAktif) return;
    const snapshot = JSON.stringify(this.parametreler?.map(p => ({ ismi: p.ismi, degeri: p.degeri })) || []);
    if (this.undoStack.length && this.undoStack[this.undoStack.length - 1] === snapshot) return;
    this.undoStack.push(snapshot);
    if (this.undoStack.length > 30) this.undoStack.shift();
    this.redoStack.length = 0;
  }

  geriAl() {
    if (this.undoStack.length < 2) return;
    const mevcut = this.undoStack.pop()!;
    this.redoStack.push(mevcut);
    const onceki = this.undoStack[this.undoStack.length - 1];
    this.undoUygula(onceki);
  }

  ileriAl() {
    if (!this.redoStack.length) return;
    const sonraki = this.redoStack.pop()!;
    this.undoStack.push(sonraki);
    this.undoUygula(sonraki);
  }

  private undoUygula(snapshot: string) {
    this.undoAktif = true;
    try {
      const degerler: { ismi: string; degeri: any }[] = JSON.parse(snapshot);
      for (const d of degerler) {
        const p = this.parametreler?.find(x => x.ismi === d.ismi);
        if (p) p.degeri = d.degeri;
      }
      this.inlineAyarDegisti();
      this.CD.detectChanges();
    } finally { this.undoAktif = false; }
  }

  get undoYapilabilir(): boolean { return this.undoStack.length > 1; }
  get redoYapilabilir(): boolean { return this.redoStack.length > 0; }

  @HostListener('document:keydown', ['$event'])
  klavyeKisayol(e: KeyboardEvent) {
    if (!e.ctrlKey && !e.metaKey) return;
    if (e.key === 'z' && !e.shiftKey) { e.preventDefault(); this.geriAl(); }
    if (e.key === 'y' || (e.key === 'z' && e.shiftKey)) { e.preventDefault(); this.ileriAl(); }
  }

  tiklandiResimAc(itm: any) {
    if (!itm?.degeri) return;
    const url = typeof itm.degeri === 'string' ? itm.degeri : itm.degeri?.url || itm.degeri?.kokDosyaObjectUrl;
    if (url) window.open(url, '_blank');
  }

}

interface ParametreBulOpsiyon {
  tamEslestir?: boolean; // default: false
  ozelFiltreler?: ((param: IParametre) => boolean)[]; // birden fazla özel filtre
}
